/* SEQ Air Care global modal booking engine with cart-style persistence. */
'use strict';


(function initialiseSEQBookingAnalytics() {
  const endpoint = 'https://google-reviews-sync-production.up.railway.app/api/public/funnel-events';
  const testStorageKey = 'seq_internal_test_mode_v1';
  const sessionStorageKey = 'seq_booking_analytics_session_v1';
  const landingStorageKey = 'seq_booking_analytics_landing_v1';
  const attributionStorageKey = 'seq_booking_analytics_attribution_v1';

  function storageGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (error) {
      // Analytics remains optional when browser storage is unavailable.
    }
  }

  function storageRemove(storage, key) {
    try {
      storage.removeItem(key);
    } catch (error) {
      // Analytics remains optional when browser storage is unavailable.
    }
  }

  function randomId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
      const random = Math.random() * 16 | 0;
      const value = character === 'x' ? random : (random & 0x3 | 0x8);
      return value.toString(16);
    });
  }

  const params = new URLSearchParams(window.location.search);
  const testParam = String(params.get('seq_test') || '').trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(testParam)) {
    storageSet(window.localStorage, testStorageKey, '1');
    // Keep internal testing completely separate from any live visit that
    // happened earlier in the same browser session.
    storageRemove(window.sessionStorage, sessionStorageKey);
    storageRemove(window.sessionStorage, landingStorageKey);
    storageRemove(window.sessionStorage, attributionStorageKey);
  } else if (['0', 'false', 'no', 'off'].includes(testParam)) {
    storageRemove(window.localStorage, testStorageKey);
    // Starting live mode again should also begin a clean live session.
    storageRemove(window.sessionStorage, sessionStorageKey);
    storageRemove(window.sessionStorage, landingStorageKey);
    storageRemove(window.sessionStorage, attributionStorageKey);
  }

  const isThemePreview = Boolean(
    window.Shopify?.designMode
    || params.has('preview_theme_id')
    || params.has('_fd')
  );
  const internalTestMode = isThemePreview || storageGet(window.localStorage, testStorageKey) === '1';
  const internalTestReason = isThemePreview
    ? 'shopify_theme_preview'
    : internalTestMode ? 'jordan_internal_browser' : '';

  const sessionId = storageGet(window.sessionStorage, sessionStorageKey) || randomId();
  storageSet(window.sessionStorage, sessionStorageKey, sessionId);

  const landingPage = storageGet(window.sessionStorage, landingStorageKey) || window.location.href;
  storageSet(window.sessionStorage, landingStorageKey, landingPage);

  function browserName() {
    const value = navigator.userAgent || '';
    if (/edg\//i.test(value)) return 'Edge';
    if (/opr\//i.test(value)) return 'Opera';
    if (/chrome\//i.test(value) && !/edg\//i.test(value)) return 'Chrome';
    if (/safari\//i.test(value) && !/chrome\//i.test(value)) return 'Safari';
    if (/firefox\//i.test(value)) return 'Firefox';
    return 'Other';
  }

  function deviceType() {
    const value = navigator.userAgent || '';
    if (/ipad|tablet|playbook|silk/i.test(value)) return 'tablet';
    if (/mobi|iphone|ipod|android/i.test(value)) return 'mobile';
    return 'desktop';
  }

  function readAttribution() {
    const stored = storageGet(window.sessionStorage, attributionStorageKey);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        // Rebuild invalid stored attribution below.
      }
    }

    const landingParams = new URL(landingPage).searchParams;
    let source = landingParams.get('utm_source') || '';
    let medium = landingParams.get('utm_medium') || '';
    if (landingParams.get('gclid')) {
      source ||= 'google';
      medium ||= 'cpc';
    }
    if (landingParams.get('fbclid')) {
      source ||= 'meta';
      medium ||= 'paid_social';
    }
    if (!source && document.referrer) {
      try {
        const hostname = new URL(document.referrer).hostname.replace(/^www\./, '');
        if (/google\./i.test(hostname)) source = 'google';
        else if (/facebook\.com|instagram\.com|fb\.com/i.test(hostname)) source = 'meta';
        else source = hostname;
      } catch (error) {
        source = '';
      }
    }

    const attribution = {
      utm_source: source || 'direct',
      utm_medium: medium,
      utm_campaign: landingParams.get('utm_campaign') || '',
      utm_content: landingParams.get('utm_content') || '',
      utm_term: landingParams.get('utm_term') || '',
      utm_id: landingParams.get('utm_id') || '',
      adset_id: landingParams.get('adset_id') || '',
      ad_id: landingParams.get('ad_id') || '',
      gclid: landingParams.get('gclid') || '',
      fbclid: landingParams.get('fbclid') || ''
    };
    storageSet(window.sessionStorage, attributionStorageKey, JSON.stringify(attribution));
    return attribution;
  }

  const attribution = readAttribution();

  window.SEQBookingInternalTest = {
    active: internalTestMode,
    reason: internalTestReason,
    isActive: () => internalTestMode,
    enableUrl: `${window.location.origin}${window.location.pathname}?seq_test=1`,
    disableUrl: `${window.location.origin}${window.location.pathname}?seq_test=0`
  };

  if (internalTestMode) {
    document.addEventListener('DOMContentLoaded', () => {
      if (document.querySelector('[data-seq-internal-test-badge]')) return;
      const badge = document.createElement('div');
      badge.setAttribute('data-seq-internal-test-badge', '');
      badge.setAttribute('role', 'status');
      badge.textContent = 'SEQ TEST MODE \u00B7 analytics and ad conversions excluded';
      Object.assign(badge.style, {
        position: 'fixed',
        left: '12px',
        bottom: '12px',
        zIndex: '2147483647',
        maxWidth: 'calc(100vw - 24px)',
        padding: '8px 11px',
        color: '#ffffff',
        background: '#071d33',
        border: '1px solid rgba(255,255,255,.2)',
        borderRadius: '999px',
        boxShadow: '0 8px 24px rgba(7,29,51,.22)',
        font: '700 10px/1.2 Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
        letterSpacing: '.04em'
      });
      document.body.appendChild(badge);
    });
  }

  function isInternalTestNow() {
    const liveParams = new URLSearchParams(window.location.search);
    const liveParam = String(liveParams.get('seq_test') || liveParams.get('seq_internal') || '')
      .trim()
      .toLowerCase();
    return internalTestMode
      || ['1', 'true', 'yes', 'on'].includes(liveParam)
      || storageGet(window.localStorage, testStorageKey) === '1';
  }

  const firstPartyTracker = {
    track(eventName, details = {}) {
        const effectiveTestMode = isInternalTestNow();
        const metadata = {
          ...attribution,
          page_title: document.title || '',
          viewport_width: String(window.innerWidth || ''),
          viewport_height: String(window.innerHeight || ''),
          device_type: deviceType(),
          browser: browserName(),
          internal_test: effectiveTestMode ? 'true' : 'false',
          test_reason: internalTestReason,
          ...(details.metadata || {})
        };

        const payload = {
          eventId: randomId(),
          sessionId,
          eventName,
          occurredAt: new Date().toISOString(),
          pageUrl: window.location.href,
          landingPage,
          source: attribution.utm_source,
          campaign: attribution.utm_campaign,
          isTest: effectiveTestMode,
          testReason: metadata.test_reason || (effectiveTestMode ? 'jordan_internal_browser' : ''),
          referrer: document.referrer || '',
          deviceType: deviceType(),
          browser: browserName(),
          ...details,
          metadata
        };

        fetch(endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify(payload)
        }).catch(() => {
          // Analytics failures must never interrupt the booking experience.
        });
    }
  };
  window.SEQBookingFirstPartyTracking = firstPartyTracker;
  window.SEQBookingTracking = firstPartyTracker;
})();

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-global-booking-app]').forEach(initBooking);
});

function initBooking(section) {
  if (section.dataset.seqBookingInitialized === 'true') return;
  section.dataset.seqBookingInitialized = 'true';

  if (!window.supabase) {
    console.error('Supabase library is unavailable.');
    return;
  }

  const supabaseClient = window.supabase.createClient(
    'https://henmmrudqnqumjgmsfec.supabase.co',
    'sb_publishable_8HIOHRksVIxaxu7gh--apQ_cAxldXsc'
  );

  const el = selector => section.querySelector(selector);
  const els = selector => Array.from(section.querySelectorAll(selector));
  const containingBookingDialog = section.closest('[data-seq-booking-dialog]');
  const offerRoot = containingBookingDialog || section;
  const offerEl = selector => offerRoot.querySelector(selector);
  const offerEls = selector => Array.from(offerRoot.querySelectorAll(selector));
  const promotionsDisabled = Boolean(
    containingBookingDialog?.dataset.disablePromotions === 'true'
    || window.SEQ_DISABLE_PROMOTIONS === true
    || /^\/pages\/manage-booking\/?$/i.test(window.location.pathname)
  );
  const winterPromotionEnabled = false;

  const inlineOfferEnabled = Boolean(
    winterPromotionEnabled &&
    !promotionsDisabled &&
    section.dataset.inlineOfferEnabled !== 'false'
  );
  const customerProfileStorageKey = 'seq_customer_profile_v1';
  const internalTestMode = Boolean(window.SEQBookingInternalTest?.isActive?.());

  const unitCountEl = el('[data-unit-count]');
  const outdoorCountEl = el('[data-outdoor-count]');
  const totalPriceEl = el('[data-total-price]');
  const summaryTotalEl = el('[data-summary-total]');
  const summaryDetailEl = el('[data-summary-detail]');
  const savingEl = el('[data-unit-saving]');
  const suburbSelect = el('[data-suburb-select]');
  const bookingDatesEl = el('[data-booking-dates]');
  const bookingTimesEl = el('[data-booking-times]');
  const bookingScrollEl = containingBookingDialog
    ?.querySelector('[data-seq-booking-scroll]') || null;
  const bookingSelectedEl = el('[data-booking-selected]');
  const bookingSelectedTextEl = el('[data-booking-selected-text]');
  const confirmNextButton = el('[data-confirm-next]');
  const addService = el('[data-add-service]');
  const mobileInput = el('[data-mobile-number]');
  const mobileError = el('[data-mobile-error]');

  const stepThreePanel = el('[data-step-panel="3"]');
  stepThreePanel?.classList.add('seq-final-step');
  const mobileField = mobileInput?.closest('.seq-pdp-field');
  const directBookingFieldId = String(section.dataset.sectionId || 'global-booking')
    .replace(/[^a-z0-9_-]/gi, '-');

  const suburbPostcodes = Object.freeze({
    "Albany Creek": "4035",
    "Albion": "4010",
    "Alderley": "4051",
    "Arana Hills": "4054",
    "Ascot": "4007",
    "Ashgrove": "4060",
    "Aspley": "4034",
    "Bald Hills": "4036",
    "Banyo": "4014",
    "Bardon": "4065",
    "Beachmere": "4510",
    "Bellmere": "4510",
    "Boondall": "4034",
    "Bowen Hills": "4006",
    "Bracken Ridge": "4017",
    "Bray Park": "4500",
    "Brendale": "4500",
    "Bridgeman Downs": "4035",
    "Brighton": "4017",
    "Bunya": "4055",
    "Burpengary": "4505",
    "Burpengary East": "4505",
    "Caboolture": "4510",
    "Caboolture South": "4510",
    "Carseldine": "4034",
    "Cashmere": "4500",
    "Chermside": "4032",
    "Chermside West": "4032",
    "Clayfield": "4011",
    "Clontarf": "4019",
    "Dakabin": "4503",
    "Deagon": "4017",
    "Deception Bay": "4508",
    "Eagle Farm": "4009",
    "Eatons Hill": "4037",
    "Elimbah": "4516",
    "Enoggera": "4051",
    "Everton Hills": "4053",
    "Everton Park": "4053",
    "Ferny Grove": "4055",
    "Ferny Hills": "4055",
    "Fitzgibbon": "4018",
    "Fortitude Valley": "4006",
    "Gaythorne": "4051",
    "Geebung": "4034",
    "Gordon Park": "4031",
    "Grange": "4051",
    "Griffin": "4503",
    "Hamilton": "4007",
    "Hendra": "4011",
    "Herston": "4006",
    "Joyner": "4500",
    "Kalinga": "4030",
    "Kallangur": "4503",
    "Kedron": "4031",
    "Kelvin Grove": "4059",
    "Keperra": "4054",
    "Kippa-Ring": "4021",
    "Lawnton": "4501",
    "Lutwyche": "4030",
    "Mango Hill": "4509",
    "Margate": "4019",
    "McDowall": "4053",
    "Mitchelton": "4053",
    "Morayfield": "4506",
    "Murrumba Downs": "4503",
    "Narangba": "4504",
    "New Farm": "4005",
    "Newmarket": "4051",
    "Newport": "4020",
    "Newstead": "4006",
    "North Lakes": "4509",
    "Northgate": "4013",
    "Nudgee": "4014",
    "Nudgee Beach": "4014",
    "Nundah": "4012",
    "Paddington": "4064",
    "Petrie": "4502",
    "Petrie Terrace": "4000",
    "Red Hill": "4059",
    "Redcliffe": "4020",
    "Rothwell": "4022",
    "Sandgate": "4017",
    "Scarborough": "4020",
    "Shorncliffe": "4017",
    "Spring Hill": "4000",
    "Stafford": "4053",
    "Stafford Heights": "4053",
    "Strathpine": "4500",
    "Taigum": "4018",
    "Teneriffe": "4005",
    "The Gap": "4061",
    "Upper Caboolture": "4510",
    "Upper Kedron": "4055",
    "Virginia": "4014",
    "Warner": "4500",
    "Wavell Heights": "4012",
    "Wilston": "4051",
    "Windsor": "4030",
    "Woody Point": "4019",
    "Wooloowin": "4030",
    "Zillmere": "4034"
});

  if (stepThreePanel && mobileField && !el('[data-customer-name]')) {
    const details = document.createElement('div');
    details.className = 'seq-direct-booking-fields';
    details.setAttribute('data-direct-booking-fields', '');
    details.innerHTML = `
      <div class="seq-pdp-field seq-final-detail-field">
        <label for="${directBookingFieldId}-customer-name">Full name <em>Required</em></label>
        <input
          type="text"
          id="${directBookingFieldId}-customer-name"
          class="seq-final-detail-input"
          placeholder="First and last name"
          autocomplete="name"
          maxlength="100"
          data-customer-name
          required
        >
        <p class="seq-field-error" data-customer-name-error hidden>Please enter your name.</p>
      </div>

      <div class="seq-pdp-field seq-final-detail-field">
        <label for="${directBookingFieldId}-service-address">Street address <em>Required</em></label>
        <input
          type="text"
          id="${directBookingFieldId}-service-address"
          class="seq-final-detail-input"
          placeholder="House number and street name"
          autocomplete="address-line1"
          maxlength="180"
          data-service-address
          required
        >
        <small>Your selected suburb and postcode are added automatically.</small>
        <p class="seq-field-error" data-service-address-error hidden>Please enter the street address where the service will take place.</p>
      </div>
    `;
    mobileField.before(details);
  }

  const customerNameInput = el('[data-customer-name]');
  const customerNameError = el('[data-customer-name-error]');
  const serviceAddressInput = el('[data-service-address]');
  const serviceAddressError = el('[data-service-address-error]');

  if (mobileField) {
    const existingMobileHelper = Array.from(mobileField.children)
      .find(node => node.tagName === 'SMALL');
    const mobileHelper = existingMobileHelper || document.createElement('small');
    mobileHelper.textContent =
      'Your confirmation and customer portal link will be sent here by SMS.';
    if (!existingMobileHelper) mobileField.appendChild(mobileHelper);
  }

  if (stepThreePanel && !el('[data-direct-booking-error]')) {
    const errorMessage = document.createElement('div');
    errorMessage.className = 'seq-direct-booking-error';
    errorMessage.setAttribute('data-direct-booking-error', '');
    errorMessage.setAttribute('role', 'alert');
    errorMessage.hidden = true;
    const stickyActions = stepThreePanel.querySelector('.seq-booking-sticky-actions');
    if (stickyActions) stickyActions.before(errorMessage);
    else stepThreePanel.appendChild(errorMessage);
  }

  const directBookingError = el('[data-direct-booking-error]');
  const checkoutNoteEl = el('.seq-checkout-note');
  const ctaSecurityEl = el('.seq-cta-security');
  const progressUnitsEl = el('[data-progress-units]');
  const progressAppointmentEl = el('[data-progress-appointment]');
  const holdTitleEl = el('[data-hold-title]');
  const holdCountdownEl = el('[data-hold-countdown]');
  const holdHelpEl = el('[data-hold-help]');
  const offerSheet = offerEl('[data-offer-sheet]');
  const offerBackdrop = offerEl('[data-offer-backdrop]');
  const winterAppliedEl = el('[data-winter-applied]');
  const firstUnitDisplayEl = el('[data-first-unit-display]');
  const mobilePriceEl = el('[data-mobile-price]');
  const stepOneAppointmentButton =
    el('[data-step-panel="1"] [data-step-next="2"]')
    || el('[data-step-next="2"]');
  let winterIndoorRowEl = null;
  let winterInlinePriceEl = null;
  let winterValuePanelEl = null;
  let winterValueApplyTimer = null;
  let conversionProofEl = null;
  let conversionProofScopeEl = null;

  function cloneProofImage(source, className, fallbackAlt) {
    if (!source) return null;

    const image = document.createElement('img');
    image.className = className;
    image.src = source.currentSrc || source.src || '';
    image.alt = source.alt || fallbackAlt;
    image.loading = 'lazy';
    image.decoding = 'async';

    if (source.srcset) image.srcset = source.srcset;
    if (source.sizes) image.sizes = source.sizes;

    return image.src ? image : null;
  }

  function buildCompactResultProof(existingProofRail) {
    const beforeSource =
      existingProofRail?.querySelector(
        '.seq-proof-card__result-pair figure:first-child img'
      )
      || existingProofRail?.querySelector(
        '.seq-proof-card__result-pair img:first-of-type'
      );

    const afterSource =
      existingProofRail?.querySelector(
        '.seq-proof-card__result-pair figure:last-child img'
      )
      || existingProofRail?.querySelector(
        '.seq-proof-card__result-pair img:last-of-type'
      );

    const beforeImage = cloneProofImage(
      beforeSource,
      'seq-conversion-proof__result-image',
      'Split system before professional cleaning'
    );
    const afterImage = cloneProofImage(
      afterSource,
      'seq-conversion-proof__result-image',
      'Split system after professional cleaning'
    );

    if (!beforeImage || !afterImage) return null;

    const card = document.createElement('article');
    card.className =
      'seq-conversion-proof__trust-card seq-conversion-proof__trust-card--result';
    card.innerHTML = `
      <div class="seq-conversion-proof__result-pair">
        <figure>
          <span>Before</span>
        </figure>
        <figure>
          <span>After</span>
        </figure>
      </div>
      <div class="seq-conversion-proof__trust-copy">
        <small>Real before & after</small>
        <strong>See the internal result of a real clean</strong>
        <p>You see the condition before cleaning and the finished result before payment.</p>
      </div>
    `;

    const figures = card.querySelectorAll('figure');
    figures[0]?.prepend(beforeImage);
    figures[1]?.prepend(afterImage);

    return card;
  }

  function buildCompactOwnerProof(existingProofRail) {
    const ownerSource =
      existingProofRail?.querySelector('.seq-proof-card--owner img')
      || section.querySelector('.seq-proof-card--owner img');

    const ownerImage = cloneProofImage(
      ownerSource,
      'seq-conversion-proof__owner-image',
      'Jordan from SEQ Air Care'
    );

    const ownerName =
      existingProofRail
        ?.querySelector('.seq-proof-card--owner .seq-proof-card__copy strong')
        ?.textContent
        ?.replace(/\s+/g, ' ')
        ?.trim()
      || 'Jordan, owner of SEQ Air Care';

    const card = document.createElement('article');
    card.className =
      'seq-conversion-proof__trust-card seq-conversion-proof__trust-card--owner';

    const media = document.createElement('div');
    media.className = 'seq-conversion-proof__owner-media';

    if (ownerImage) {
      media.appendChild(ownerImage);
    } else {
      media.innerHTML = '<span aria-hidden="true">J</span>';
    }

    const copy = document.createElement('div');
    copy.className = 'seq-conversion-proof__trust-copy';
    copy.innerHTML = `
      <small>You book directly with me</small>
      <strong>${ownerName}</strong>
      <p>I complete every clean myself and treat your home with care.</p>
      <div class="seq-conversion-proof__owner-trust">
        <span>Fully insured</span>
        <span>Before and after proof</span>
        <span>Pay after the clean</span>
      </div>
    `;

    card.append(media, copy);
    return card;
  }


  function configureProofVideo(videoSource) {
    if (!videoSource) return null;

    videoSource.hidden = false;
    videoSource.removeAttribute('hidden');
    videoSource.classList.add('seq-booking-proof-video');

    const video = videoSource.querySelector('video');
    const playButton = videoSource.querySelector('[data-booking-proof-video-play]');
    const reviewsButton = videoSource.querySelector('[data-booking-proof-video-reviews]');

    if (!video || !playButton) return videoSource;

    /*
      Keep the video in a contained, non-interactive media layer. On iOS Safari,
      a tappable video element can create its own compositing layer and capture
      touches outside the visible media area. All interaction is routed through
      the dedicated play button instead.
    */
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.controls = false;
    video.disablePictureInPicture = true;
    video.preload = 'metadata';
    video.tabIndex = -1;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');

    const updateVideoState = () => {
      const isPlaying = !video.paused && !video.ended;
      videoSource.classList.toggle('is-playing', isPlaying);
      playButton.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
      playButton.setAttribute(
        'aria-label',
        isPlaying ? 'Pause booking proof video' : 'Play booking proof video'
      );

      const label = playButton.querySelector('span');
      if (label) label.textContent = isPlaying ? 'Pause' : 'Watch the clean';
    };

    const toggleVideo = async event => {
      event?.preventDefault();
      event?.stopPropagation();

      try {
        if (video.paused || video.ended) {
          await video.play();
          videoSource.classList.remove('has-video-error');
          pushOfferEvent('seq_booking_proof_video_play', { source: 'step_one' });
        } else {
          video.pause();
          pushOfferEvent('seq_booking_proof_video_pause', { source: 'step_one' });
        }
      } catch (error) {
        videoSource.classList.add('has-video-error');
        const label = playButton.querySelector('span');
        if (label) label.textContent = 'Tap to retry';
        console.warn('Booking proof video could not play:', error);
      }

      updateVideoState();
    };

    if (videoSource.dataset.videoReady !== 'true') {
      videoSource.dataset.videoReady = 'true';
      playButton.addEventListener('click', toggleVideo);
      video.addEventListener('play', updateVideoState);
      video.addEventListener('pause', updateVideoState);
      video.addEventListener('ended', updateVideoState);

      reviewsButton?.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        containingBookingDialog?.querySelector('[data-open-booking-reviews]')?.click();
      });
    }

    updateVideoState();
    return videoSource;
  }


  function configureProgressiveProof(existingProofRail) {
    if (!conversionProofEl || conversionProofEl.dataset.progressiveProofReady === 'true') return;
    conversionProofEl.dataset.progressiveProofReady = 'true';

    const beforeSource = existingProofRail?.querySelector('.seq-proof-card__result-pair figure:first-child img')
      || existingProofRail?.querySelector('.seq-proof-card__result-pair img:first-of-type');
    const afterSource = existingProofRail?.querySelector('.seq-proof-card__result-pair figure:last-child img')
      || existingProofRail?.querySelector('.seq-proof-card__result-pair img:last-of-type');

    const snapshot = document.createElement('section');
    snapshot.className = 'seq-proof-snapshot';
    snapshot.setAttribute('aria-label', 'Quick booking reassurance');
    snapshot.innerHTML = `
      <div class="seq-proof-snapshot__visual" aria-hidden="true">
        <figure><span>Before</span></figure>
        <figure><span>After</span></figure>
      </div>
      <div class="seq-proof-snapshot__copy">
        <strong>See a real before and after</strong>
        <small>You see the internal result before payment.</small>
        <button type="button" data-progressive-reviews><span aria-hidden="true">\u2605\u2605\u2605\u2605\u2605</span> Read local Google reviews</button>
      </div>
    `;

    const figures = snapshot.querySelectorAll('figure');
    const beforeImage = cloneProofImage(beforeSource, 'seq-proof-snapshot__image', 'Air conditioner before cleaning');
    const afterImage = cloneProofImage(afterSource, 'seq-proof-snapshot__image', 'Air conditioner after cleaning');
    if (beforeImage) figures[0]?.prepend(beforeImage);
    if (afterImage) figures[1]?.prepend(afterImage);
    if (!beforeImage || !afterImage) snapshot.classList.add('is-text-only');

    snapshot.querySelector('[data-progressive-reviews]')?.addEventListener('click', () => {
      containingBookingDialog?.querySelector('[data-open-booking-reviews]')?.click();
    });

    const expanded = document.createElement('details');
    expanded.className = 'seq-progressive-proof';
    expanded.innerHTML = `
      <summary>
        <span><strong>See exactly what\u2019s included</strong><small>The full process, before and after proof, and what you can expect</small></span>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
      </summary>
      <div class="seq-progressive-proof__body"></div>
    `;

    const body = expanded.querySelector('.seq-progressive-proof__body');
    const problemSection = conversionProofEl.querySelector(
      '.seq-conversion-proof__benefits'
    );
    const serviceFit = conversionProofEl.querySelector(
      '.seq-conversion-proof__service-fit'
    );

    /*
      Keep the problem/fit messaging visible on Step 1. Only the deeper process,
      trust and owner proof are progressively disclosed. This changes presentation
      only and deliberately leaves all booking, pricing and reservation logic alone.
    */
    Array.from(conversionProofEl.children)
      .filter(child => child !== problemSection && child !== serviceFit)
      .forEach(child => body.appendChild(child));

    const videoProof = configureProofVideo(
      section.querySelector('[data-booking-proof-video-source]')
    );

    if (problemSection) conversionProofEl.appendChild(problemSection);
    if (serviceFit) conversionProofEl.appendChild(serviceFit);
    conversionProofEl.append(videoProof || snapshot, expanded);

    expanded.addEventListener('toggle', () => {
      pushOfferEvent('seq_booking_proof_toggle', { expanded: expanded.open });
    });
  }

  function removeLegacyWinterOfferContainers() {
    section.querySelectorAll(
      '.seq-winter-value-panel, .seq-winter-inline-price, [data-winter-applied]'
    ).forEach(element => {
      if (element.hasAttribute('data-winter-applied')) {
        element.hidden = true;
      } else {
        element.remove();
      }
    });

    winterValuePanelEl = null;
    winterInlinePriceEl = null;
    winterIndoorRowEl?.classList.remove('has-winter-price');
    section.classList.remove(
      'has-winter-value-panel',
      'has-active-winter-value',
      'has-standard-booking-value'
    );
  }

  function ensureInlineOffer() {
    removeLegacyWinterOfferContainers();
    if (!inlineOfferEnabled || inlineOfferEl) return;

    const stepOnePanel = el('[data-step-panel="1"]');
    if (!stepOnePanel) return;

    const target =
      conversionProofEl
      || stepOnePanel.querySelector('.seq-booking-sticky-actions')
      || stepOnePanel.querySelector('.seq-step-nav');
    if (!target) return;

    inlineOfferEl = document.createElement('section');
    inlineOfferEl.className = 'seq-inline-offer';
    inlineOfferEl.setAttribute('data-inline-offer', '');
    inlineOfferEl.setAttribute('aria-live', 'polite');
    inlineOfferEl.innerHTML = `
      <span class="seq-inline-offer__icon" aria-hidden="true">\u2744</span>
      <div class="seq-inline-offer__copy">
        <small>Limited time \u00B7 WINTER</small>
        <strong>FREE outdoor unit clean</strong>
        <span>Book any split system clean and add its outdoor unit clean FREE. Every selected outdoor clean is free with WINTER.</span>
      </div>
      <button type="button" data-inline-offer-apply>Apply free outdoor clean</button>
    `;

    target.insertAdjacentElement('beforebegin', inlineOfferEl);
    inlineOfferEl
      .querySelector('[data-inline-offer-apply]')
      ?.addEventListener('click', () => {
        applyWinterSpecial({ source: 'inline_offer', scroll: false });
      });

    updateInlineOffer();
  }

  function updateInlineOffer() {
    if (!inlineOfferEl) return;

    removeLegacyWinterOfferContainers();
    const copy = inlineOfferEl.querySelector('.seq-inline-offer__copy');
    const button = inlineOfferEl.querySelector('[data-inline-offer-apply]');
    const icon = inlineOfferEl.querySelector('.seq-inline-offer__icon');

    inlineOfferEl.classList.toggle('is-applied', winterSpecialApplied);

    if (winterSpecialApplied) {
      if (icon) icon.textContent = '\u2713';
      if (copy) {
        copy.innerHTML = '<strong>WINTER applied \u00B7 Outdoor unit cleaning FREE</strong>';
      }
      if (button) {
        button.textContent = '\u2713 Applied';
        button.disabled = true;
        button.hidden = true;
        button.setAttribute('aria-label', 'WINTER offer applied');
      }
      return;
    }

    if (icon) icon.textContent = '\u2744';
    if (copy) {
      copy.innerHTML = `
        <small>Limited time \u00B7 WINTER</small>
        <strong>FREE outdoor unit clean</strong>
        <span>Book any split system clean and add its outdoor unit clean FREE. Every selected outdoor clean is free with WINTER.</span>
      `;
    }
    if (button) {
      button.hidden = false;
      button.textContent = 'Apply free outdoor clean';
      button.disabled = false;
      button.setAttribute('aria-label', 'Apply WINTER for free outdoor unit cleaning');
    }
  }

  function ensureReturningCustomerBanner({ rebook = false } = {}) {
    const profile = returningCustomerProfile;
    if (!profile && !rebook) return;
    const stepOnePanel = el('[data-step-panel="1"]');
    const intro = stepOnePanel?.querySelector('.seq-step-intro');
    if (!stepOnePanel || !intro) return;

    if (!returningCustomerBannerEl) {
      returningCustomerBannerEl = document.createElement('section');
      returningCustomerBannerEl.className = 'seq-returning-customer';
      returningCustomerBannerEl.setAttribute('data-returning-customer', '');
      intro.insertAdjacentElement('afterend', returningCustomerBannerEl);
    }

    const firstName = cleanCustomerName(bookingContext.customerName || profile?.name || '').split(' ')[0];
    const lastIndoor = Number(profile?.indoorUnits || 1);
    const lastOutdoor = Number(profile?.outdoorUnits || 0);
    returningCustomerBannerEl.innerHTML = `
      <span class="seq-returning-customer__icon" aria-hidden="true">\u2713</span>
      <div>
        <small>${rebook ? 'Rebooking made easy' : 'Welcome back'}</small>
        <strong>${firstName ? `${firstName}, your details are ready` : 'Your saved details are ready'}</strong>
        <p>${rebook ? 'Your previous service quantities have been preselected. Review them before continuing.' : 'I\u2019ve prefilled your contact details on this device. You can review them before confirming.'}</p>
      </div>
      ${profile ? `<button type="button" data-use-last-service>Use last service</button>` : ''}
    `;

    returningCustomerBannerEl.querySelector('[data-use-last-service]')?.addEventListener('click', () => {
      units = Math.max(1, Math.min(10, lastIndoor));
      outdoorUnits = Math.max(0, Math.min(units, lastOutdoor));
      updatePricing();
      resetReservation(false);
      void loadNextAvailableDates();
      pushOfferEvent('seq_booking_last_service_applied', { indoor_units: units, outdoor_units: outdoorUnits });
    }, { once: true });
  }

  function ensureSuburbSearch() {
    if (!suburbSelect || suburbSearchInput) return;

    const field = suburbSelect.closest('.seq-pdp-field');
    if (!field) return;

    const listId = `seq-suburbs-${directBookingFieldId}`;
    const wrapper = document.createElement('div');
    wrapper.className = 'seq-suburb-combobox';
    wrapper.innerHTML = `
      <input
        type="search"
        id="${directBookingFieldId}-suburb-search"
        class="seq-suburb-search"
        placeholder="Enter suburb or postcode"
        autocomplete="off"
        enterkeyhint="done"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded="false"
        aria-controls="${listId}"
        data-suburb-search
      >
      <div id="${listId}" class="seq-suburb-suggestions" role="listbox" hidden></div>
      <span class="seq-suburb-combobox__status" data-suburb-search-status>
        Enter your suburb or postcode
      </span>
    `;

    suburbSelect.classList.add('seq-suburb-native-select');
    suburbSelect.insertAdjacentElement('beforebegin', wrapper);

    const fieldLabel = field.querySelector('label');
    if (fieldLabel) {
      const icon = fieldLabel.querySelector('.seq-svg')?.cloneNode(true);
      const required = fieldLabel.querySelector('em')?.cloneNode(true);
      fieldLabel.replaceChildren();
      if (icon) fieldLabel.appendChild(icon);
      fieldLabel.appendChild(document.createTextNode('Your suburb'));
      if (required) fieldLabel.appendChild(required);
      fieldLabel.setAttribute('for', `${directBookingFieldId}-suburb-search`);
    }

    suburbSearchInput = wrapper.querySelector('[data-suburb-search]');
    const status = wrapper.querySelector('[data-suburb-search-status]');
    const suggestions = wrapper.querySelector('.seq-suburb-suggestions');
    let blurTimer = null;
    let selectionTimer = null;
    let skipNextBlurCommit = false;

    const availableOptions = () =>
      Array.from(suburbSelect.options || []).filter(option => option.value);

    const hideSuggestions = () => {
      if (!suggestions || !suburbSearchInput) return;
      suggestions.hidden = true;
      suggestions.replaceChildren();
      suburbSearchInput.setAttribute('aria-expanded', 'false');
    };

    const syncFromSelect = () => {
      const selected = suburbSelect.value || '';
      if (suburbSearchInput) suburbSearchInput.value = selected;

      const postcode = suburbPostcodes[selected] || '';
      if (status) {
        status.textContent = selected
          ? `\u2713 ${selected}${postcode ? ` \u00B7 ${postcode}` : ''} is in my service area`
          : 'Enter your suburb or postcode';
      }
      wrapper.classList.toggle('is-valid', Boolean(selected));
      wrapper.classList.remove('is-ready');
    };

    const matchingOptions = value => {
      const wanted = String(value || '').trim().toLowerCase();
      if (!wanted) return [];

      return availableOptions()
        .filter(option => {
          const suburb = option.value.toLowerCase();
          const postcode = suburbPostcodes[option.value] || '';
          return suburb.includes(wanted) || postcode.startsWith(wanted);
        })
        .slice(0, 8);
    };

    const renderSuggestions = value => {
      if (!suggestions || !suburbSearchInput) return [];
      const matches = matchingOptions(value);
      suggestions.replaceChildren();

      matches.forEach(option => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'seq-suburb-suggestion';
        item.setAttribute('role', 'option');
        item.dataset.suburbSuggestion = option.value;
        item.innerHTML = `
          <strong>${option.value}</strong>
          <span>${suburbPostcodes[option.value] || ''}</span>
        `;
        item.addEventListener('pointerdown', event => event.preventDefault());
        item.addEventListener('click', () => selectOption(option));
        suggestions.appendChild(item);
      });

      suggestions.hidden = matches.length === 0;
      suburbSearchInput.setAttribute(
        'aria-expanded',
        matches.length > 0 ? 'true' : 'false'
      );
      return matches;
    };

    const dispatchSelectedSuburb = changed => {
      if (!changed) return;
      suburbSelect.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const dismissKeyboardThen = callback => {
      if (document.activeElement === suburbSearchInput) {
        skipNextBlurCommit = true;
        suburbSearchInput.blur();
        if (selectionTimer) window.clearTimeout(selectionTimer);
        selectionTimer = window.setTimeout(callback, 110);
      } else {
        callback();
      }
    };

    const selectOption = option => {
      if (!option) return;

      const changed = suburbSelect.value !== option.value;
      suburbSelect.value = option.value;
      syncFromSelect();
      hideSuggestions();

      /* Close the mobile keyboard first, then begin the availability request. */
      dismissKeyboardThen(() => dispatchSelectedSuburb(changed));
    };

    const commitSearch = ({ dismissKeyboard = false } = {}) => {
      const typed = suburbSearchInput?.value.trim() || '';
      const match = matchingSuburbOption(typed);

      if (match) {
        selectOption(match);
        return true;
      }

      const matches = matchingOptions(typed);
      const exactPostcodeMatches = /^\d{4}$/.test(typed)
        ? matches.filter(option => suburbPostcodes[option.value] === typed)
        : [];

      wrapper.classList.remove('is-valid', 'is-ready');
      if (status) {
        if (exactPostcodeMatches.length > 1) {
          status.textContent = `Choose your suburb for postcode ${typed}`;
        } else if (typed) {
          status.textContent = matches.length
            ? 'Choose your suburb from the suggestions'
            : 'That suburb or postcode is outside my current service area';
        } else {
          status.textContent = 'Enter your suburb or postcode';
        }
      }
      renderSuggestions(typed);

      if (dismissKeyboard) {
        skipNextBlurCommit = true;
        suburbSearchInput?.blur();
      }
      return false;
    };

    suburbSearchInput?.addEventListener('input', () => {
      const typed = suburbSearchInput.value.trim();
      const exactMatch = matchingSuburbOption(typed);

      /*
        Do not load dates while the customer is still typing. An exact match is
        presented as ready, then committed once they press Enter, choose a
        suggestion or leave the field.
      */
      if (suburbSelect.value && suburbSelect.value !== exactMatch?.value) {
        suburbSelect.value = '';
        suburbSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      wrapper.classList.remove('is-valid');
      wrapper.classList.toggle('is-ready', Boolean(exactMatch));
      const matches = renderSuggestions(typed);

      if (status) {
        if (!typed) {
          status.textContent = 'Enter your suburb or postcode';
        } else if (/^\d{4}$/.test(typed) && matches.filter(
          option => suburbPostcodes[option.value] === typed
        ).length > 1) {
          status.textContent = `Choose your suburb for postcode ${typed}`;
        } else if (exactMatch) {
          status.textContent = `Press done to use ${exactMatch.value}`;
        } else {
          status.textContent = matches.length
            ? 'Choose your suburb from the suggestions'
            : 'Keep typing your suburb or postcode';
        }
      }
    });

    suburbSearchInput?.addEventListener('focus', () => {
      if (suburbSearchInput.value.trim() && !suburbSelect.value) {
        renderSuggestions(suburbSearchInput.value);
      }
    });

    suburbSearchInput?.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        hideSuggestions();
        skipNextBlurCommit = true;
        suburbSearchInput.blur();
        return;
      }

      if (event.key === 'ArrowDown') {
        const first = suggestions?.querySelector('button');
        if (first) {
          event.preventDefault();
          first.focus();
        }
        return;
      }

      if (event.key !== 'Enter') return;

      event.preventDefault();
      const typed = suburbSearchInput.value.trim();
      const matches = matchingOptions(typed);
      const exactPostcodeMatches = /^\d{4}$/.test(typed)
        ? matches.filter(option => suburbPostcodes[option.value] === typed)
        : [];

      if (exactPostcodeMatches.length > 1) {
        renderSuggestions(typed);
        skipNextBlurCommit = true;
        suburbSearchInput.blur();
        window.requestAnimationFrame(() => {
          suggestions?.querySelector('button')?.focus({ preventScroll: true });
        });
        return;
      }

      const match = matchingSuburbOption(typed) || (matches.length === 1 ? matches[0] : null);
      if (match) {
        selectOption(match);
        return;
      }

      commitSearch({ dismissKeyboard: true });
    });

    suburbSearchInput?.addEventListener('blur', () => {
      if (blurTimer) window.clearTimeout(blurTimer);

      if (skipNextBlurCommit) {
        skipNextBlurCommit = false;
        hideSuggestions();
        return;
      }

      blurTimer = window.setTimeout(() => {
        const selected = suburbSelect.value || '';
        const typed = suburbSearchInput?.value.trim() || '';

        if (!selected || typed.toLowerCase() !== selected.toLowerCase()) {
          commitSearch();
        }
        hideSuggestions();
      }, 140);
    });

    suggestions?.addEventListener('focusin', () => {
      if (blurTimer) window.clearTimeout(blurTimer);
    });

    suburbSelect.addEventListener('change', syncFromSelect);
    syncFromSelect();
  }

  function buildExperienceLayout() {
    if (experienceLayoutEl) return;
    const bookingRoot = el('[data-seq-pdp-booking]') || section.querySelector('.seq-pdp-booking');
    const progress = bookingRoot?.querySelector('[data-typeform-progress]') || bookingRoot?.querySelector('.seq-typeform-progress');
    const stage = bookingRoot?.querySelector('.seq-typeform-stage');
    if (!bookingRoot || !progress || !stage || progress.parentElement !== bookingRoot || stage.parentElement !== bookingRoot) return;

    const children = Array.from(bookingRoot.children);
    const progressIndex = children.indexOf(progress);
    if (progressIndex < 0) return;

    experienceLayoutEl = document.createElement('div');
    experienceLayoutEl.className = 'seq-booking-experience';
    experienceLayoutEl.setAttribute('data-booking-experience', '');
    const rail = document.createElement('aside');
    rail.className = 'seq-booking-experience__rail';
    const main = document.createElement('div');
    main.className = 'seq-booking-experience__main';

    children.slice(0, progressIndex).forEach(node => rail.appendChild(node));
    children.slice(progressIndex).forEach(node => main.appendChild(node));

    const liveSummary = document.createElement('section');
    liveSummary.className = 'seq-live-booking-summary';
    liveSummary.innerHTML = `
      <span>Your clean</span>
      <strong data-live-service-summary></strong>
      <small data-live-appointment-summary></small>
      <div><b data-live-price-summary></b><em data-live-price-note>Pay after service</em></div>
    `;
    rail.appendChild(liveSummary);
    experienceLayoutEl.append(rail, main);
    bookingRoot.appendChild(experienceLayoutEl);
  }

  function ensureStickyContexts() {
    els('[data-step-panel]').forEach(panel => {
      const actions = panel.querySelector('.seq-booking-sticky-actions') || panel.querySelector('.seq-step-nav');
      if (!actions || actions.querySelector('[data-sticky-booking-context]')) return;
      const context = document.createElement('div');
      context.className = 'seq-sticky-booking-context';
      context.setAttribute('data-sticky-booking-context', '');
      context.innerHTML = '<span data-sticky-service></span><strong data-sticky-price></strong><small data-sticky-appointment></small>';
      actions.prepend(context);
    });
  }

  function updateExperienceSummaries() {
    const service = [plural(units, 'indoor unit')];
    if (outdoorUnits > 0) service.push(plural(outdoorUnits, 'outdoor unit'));
    const serviceText = service.join(' + ');
    const liveServiceText = outdoorUnits > 0
      ? `${units} indoor \u00B7 ${outdoorUnits} outdoor`
      : `${units} indoor`;
    const appointmentText = selectedDate && selectedTime
      ? `${formatDateFromISO(selectedDate)} at ${formatTime(selectedTime)}`
      : suburbSelect?.value
        ? `${suburbSelect.value} \u00B7 Choose an appointment`
        : 'Choose your suburb and appointment';
    const priceText = formatMoney(totalPrice());

    const priceNote = winterSpecialApplied && outdoorUnits > 0
      ? 'Outdoor cleans FREE'
      : 'Pay after service';

    section.querySelectorAll('[data-live-service-summary]').forEach(node => { node.textContent = liveServiceText; });
    section.querySelectorAll('[data-sticky-service]').forEach(node => { node.textContent = serviceText; });
    section.querySelectorAll('[data-live-appointment-summary], [data-sticky-appointment]').forEach(node => { node.textContent = appointmentText; });
    section.querySelectorAll('[data-live-price-summary], [data-sticky-price]').forEach(node => { node.textContent = priceText; });
    section.querySelectorAll('[data-live-price-note]').forEach(node => { node.textContent = priceNote; });
  }

  function configureDateProgressiveDisclosure() {
    if (!bookingDatesEl) return;
    dateShowMoreButton?.remove();
    dateShowMoreButton = null;
    const buttons = Array.from(bookingDatesEl.querySelectorAll('.seq-booking-date'));
    buttons.forEach((button, index) => {
      button.classList.toggle('is-date-hidden', index >= 7);
      button.classList.toggle('is-soonest', index === 0);
      if (index === 0 && !button.querySelector('.seq-booking-date__soonest')) {
        const badge = document.createElement('em');
        badge.className = 'seq-booking-date__soonest';
        badge.textContent = 'Recommended';
        button.prepend(badge);
      }
    });
    if (buttons.length <= 7) return;
    dateShowMoreButton = document.createElement('button');
    dateShowMoreButton.type = 'button';
    dateShowMoreButton.className = 'seq-date-show-more';
    dateShowMoreButton.textContent = `Show ${buttons.length - 7} more dates`;
    dateShowMoreButton.addEventListener('click', () => {
      buttons.forEach(button => button.classList.remove('is-date-hidden'));
      dateShowMoreButton?.remove();
      dateShowMoreButton = null;
    });
    bookingDatesEl.appendChild(dateShowMoreButton);
  }

  function configureStepOneConversionProof() {
    const stepOnePanel = el('[data-step-panel="1"]');
    if (!stepOnePanel || conversionProofEl) return;

    const actions =
      stepOnePanel.querySelector('.seq-booking-sticky-actions')
      || stepOnePanel.querySelector('.seq-step-nav');

    if (!actions) return;

    const existingDetails =
      stepOnePanel.querySelector('.seq-compact-details');

    const existingProofRail =
      stepOnePanel.querySelector('.seq-booking-proof-rail--after-selection')
      || stepOnePanel.querySelector('.seq-booking-proof-rail');

    conversionProofEl = document.createElement('section');
    conversionProofEl.className = 'seq-conversion-proof';
    conversionProofEl.setAttribute('data-conversion-proof', '');
    conversionProofEl.setAttribute(
      'aria-labelledby',
      `seq-conversion-proof-title-${section.dataset.sectionId || 'booking'}`
    );

    conversionProofEl.innerHTML = `
      <section class="seq-conversion-proof__benefits">
        <header class="seq-conversion-proof__header">
          <span>Is this what you\u2019re noticing?</span>
          <h4 id="seq-conversion-proof-title-${section.dataset.sectionId || 'booking'}">
            Common reasons people book a deep clean
          </h4>
          <p>Musty smells, dark buildup, dust or restricted airflow can point to contamination deeper than the filters.</p>
        </header>

        <div class="seq-conversion-proof__outcomes" aria-label="Common signs that an internal split system clean may be appropriate">
          <article>
            <span class="seq-conversion-proof__benefit-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M8 4c0 2-2 2.5-2 5a4 4 0 0 0 8 0c0-2.5-2-3-2-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
                <path d="M5 18c2-2 12-2 14 0" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
              </svg>
            </span>
            <div>
              <strong>Musty or stale smell</strong>
            </div>
          </article>

          <article>
            <span class="seq-conversion-proof__benefit-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <circle cx="7" cy="8" r="1.5" fill="none" stroke="currentColor" stroke-width="1.8"></circle>
                <circle cx="15.5" cy="6.5" r="2" fill="none" stroke="currentColor" stroke-width="1.8"></circle>
                <circle cx="17" cy="15" r="1.5" fill="none" stroke="currentColor" stroke-width="1.8"></circle>
                <circle cx="8" cy="17" r="2.2" fill="none" stroke="currentColor" stroke-width="1.8"></circle>
              </svg>
            </span>
            <div>
              <strong>Dark or mould-like buildup</strong>
            </div>
          </article>

          <article>
            <span class="seq-conversion-proof__benefit-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M4 6h16v5H4zM6 14h12M8 18h8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </span>
            <div>
              <strong>Clean filters, dirty internals</strong>
            </div>
          </article>

          <article>
            <span class="seq-conversion-proof__benefit-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M4 8h9a3 3 0 1 0-3-3" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"></path>
                <path d="M4 13h14a3 3 0 1 1-3 3" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"></path>
                <path d="M4 18h6" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"></path>
              </svg>
            </span>
            <div>
              <strong>Weak airflow with visible buildup</strong>
            </div>
          </article>
        </div>
      </section>

      <div class="seq-booking-guide seq-conversion-proof__service-fit" aria-label="When air conditioner cleaning may not be the right service">
        <span class="seq-booking-guide__eyebrow">Not sure cleaning is the right service?</span>
        <strong>Cleaning treats internal buildup, not faults.</strong>
        <p>Fault codes, electrical smells, repeated tripping or poor cooling can need an air-conditioning or electrical technician.</p>
      </div>

      <section class="seq-conversion-proof__outcome-section" aria-labelledby="seq-booking-outcome-title-${section.dataset.sectionId || 'booking'}">
        <header>
          <span>What you\u2019re booking</span>
          <h5 id="seq-booking-outcome-title-${section.dataset.sectionId || 'booking'}">More than a filter clean</h5>
          <p>I deep clean the accessible parts that collect buildup deeper inside your split system, not just the filters.</p>
        </header>

        <div class="seq-conversion-proof__result-outcomes" aria-label="What a professional internal split system clean is designed to improve">
          <article>
            <span aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M4 9h10a3 3 0 1 0-3-3M4 14h14a3 3 0 1 1-3 3M4 19h7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>
            </span>
            <div>
              <strong>Fresher air when it runs</strong>
              <small>Dust, grime and buildup are removed from the internal airflow path.</small>
            </div>
          </article>

          <article>
            <span aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M5 7h14v10H5zM8 10h8M8 14h5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
            </span>
            <div>
              <strong>A genuinely cleaner unit inside</strong>
              <small>Accessible coil, fan barrel, drain tray, airflow path, filters, covers and removable parts.</small>
            </div>
          </article>

          <article>
            <span aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M4 8h9a3 3 0 1 0-3-3M4 13h14a3 3 0 1 1-3 3M4 18h6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>
            </span>
            <div>
              <strong>Better airflow where buildup is restricting it</strong>
              <small>Cleaning can help when dirt on the coil or fan is interfering with normal airflow.</small>
            </div>
          </article>

          <article>
            <span aria-hidden="true">
              <svg viewBox="0 0 24 24"><rect x="4" y="6" width="16" height="12" rx="2" fill="none" stroke="currentColor" stroke-width="1.8"></rect><circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8"></circle><path d="M8 6l1-2h6l1 2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path></svg>
            </span>
            <div>
              <strong>Proof you can actually see</strong>
              <small>I provide before-and-after photos so you can see exactly what was cleaned.</small>
            </div>
          </article>
        </div>
      </section>
    `;

    conversionProofScopeEl = null;

    if (existingDetails) {
      existingDetails.removeAttribute('open');
      existingDetails.classList.add('seq-conversion-proof__details');

      const summary = existingDetails.querySelector('summary');

      if (summary) {
        summary.setAttribute(
          'aria-label',
          'See the full service timeline for your air conditioner clean'
        );
        summary.replaceChildren();
        summary.innerHTML = `
          <span class="seq-conversion-proof__accordion-heading">
            <svg class="seq-conversion-proof__accordion-list" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 6h10M9 12h10M9 18h10" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"></path>
              <circle cx="5" cy="6" r="1" fill="currentColor"></circle>
              <circle cx="5" cy="12" r="1" fill="currentColor"></circle>
              <circle cx="5" cy="18" r="1" fill="currentColor"></circle>
            </svg>
            <span>
              <b>What happens during your deep clean</b>
              <small>See the full service timeline</small>
            </span>
          </span>
          <svg class="seq-conversion-proof__accordion-chevron" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        `;

        const process = document.createElement('ol');
        process.className = 'seq-conversion-proof__process seq-conversion-proof__timeline';
        process.innerHTML = `
          <li>
            <span>1</span>
            <div>
              <strong>Inspect the system</strong>
              <p>I inspect the air conditioner first so I understand its condition before cleaning begins.</p>
            </div>
          </li>
          <li>
            <span>2</span>
            <div>
              <strong>Isolate the power</strong>
              <p>The unit is safely powered down before I work on the internal components.</p>
            </div>
          </li>
          <li>
            <span>3</span>
            <div>
              <strong>Prepare and protect the space</strong>
              <p>I protect the area beneath and around the unit to keep your home clean throughout the service.</p>
            </div>
          </li>
          <li>
            <span>4</span>
            <div>
              <strong>Remove filters, outer case and louvers</strong>
              <p>I open the indoor unit so I can reach the areas a normal filter clean cannot.</p>
            </div>
          </li>
          <li>
            <span>5</span>
            <div>
              <strong>Clean all removable parts</strong>
              <p>I clean the filters, covers and removable components before they go back on.</p>
            </div>
          </li>
          <li>
            <span>6</span>
            <div>
              <strong>Deep clean the internals</strong>
              <p>I deep clean the accessible coil, fan barrel, drain tray and internal airflow path, then check the drain path.</p>
            </div>
          </li>
          <li>
            <span>7</span>
            <div>
              <strong>Reassemble everything</strong>
              <p>I reinstall the cleaned parts correctly once the internal clean is complete.</p>
            </div>
          </li>
          <li>
            <span>8</span>
            <div>
              <strong>Clean up</strong>
              <p>I tidy the work area so your home is left clean when the job is done.</p>
            </div>
          </li>
          <li>
            <span>9</span>
            <div>
              <strong>Test the system</strong>
              <p>I test the unit for airflow and general operation before I finish up.</p>
            </div>
          </li>
          <li>
            <span>10</span>
            <div>
              <strong>See the finished result</strong>
              <p>I show you before-and-after proof so you can clearly see what changed inside the system.</p>
            </div>
          </li>
        `;

        const resultProof = buildCompactResultProof(existingProofRail);

        if (resultProof) {
          resultProof.classList.add(
            'seq-conversion-proof__accordion-proof'
          );
          existingDetails.replaceChildren(
            summary,
            process,
            resultProof
          );
        } else {
          existingDetails.replaceChildren(summary, process);
        }
      }

      conversionProofEl
        .querySelector('.seq-conversion-proof__outcome-section')
        ?.insertAdjacentElement('afterend', existingDetails);
    }

    const ownerProof = buildCompactOwnerProof(existingProofRail);

    if (ownerProof) {
      ownerProof.classList.add(
        'seq-conversion-proof__owner-card'
      );

      conversionProofEl
        .querySelector('.seq-conversion-proof__outcome-section')
        ?.insertAdjacentElement('afterend', ownerProof);
    }

    if (existingProofRail) {
      existingProofRail.hidden = true;
      existingProofRail.setAttribute('aria-hidden', 'true');
      existingProofRail.classList.add('is-replaced-by-conversion-proof');
    }

    actions.insertAdjacentElement('beforebegin', conversionProofEl);
    configureProgressiveProof(existingProofRail);
    ensureInlineOffer();

    conversionProofEl
      .querySelector('.seq-conversion-proof__details')
      ?.addEventListener('toggle', event => {
        const details = event.currentTarget;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'seq_booking_inclusions_toggle',
          expanded: details.open
        });

        if (typeof window.clarity === 'function') {
          window.clarity(
            'event',
            details.open
              ? 'seq_booking_process_open'
              : 'seq_booking_process_close'
          );
        }
      });
  }

  function updateStepOneConversionProof() {
    if (!conversionProofEl) {
      configureStepOneConversionProof();
    }
  }

  function ensureActionReassurance(button, attribute, text) {
    if (!button) return;

    const primaryWrapper = button.closest(
      '.seq-booking-sticky-actions__primary'
    );

    if (!primaryWrapper) return;

    let reassurance = primaryWrapper.querySelector(`[${attribute}]`);

    if (!reassurance) {
      reassurance = document.createElement('small');
      reassurance.className =
        'seq-booking-sticky-actions__reassurance';
      reassurance.setAttribute(attribute, '');
      primaryWrapper.appendChild(reassurance);
    }

    reassurance.textContent = text;
  }

  function configureConfirmHeroIcon() {
    const icon = el('.seq-confirm-hero__icon');
    if (!icon) return;

    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = `
      <svg viewBox="0 0 24 24">
        <path d="M6.5 12.5l3.4 3.4L17.8 8" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
    `;
  }

  function configureFinalReassurance() {
    const reassurance = el('.seq-final-reassurance');
    if (!reassurance || reassurance.dataset.seqFinalTrustReady === 'true') return;

    reassurance.dataset.seqFinalTrustReady = 'true';
    reassurance.setAttribute(
      'aria-label',
      'Flexible online booking and service guarantees'
    );

    reassurance.innerHTML = `
      <div class="seq-final-reassurance__item">
        <span class="seq-final-reassurance__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <rect x="3.5" y="6" width="17" height="12" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.8"></rect>
            <path d="M3.5 10h17M7 14h4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
          </svg>
        </span>
        <strong>No card or upfront payment</strong>
      </div>

      <div class="seq-final-reassurance__item">
        <span class="seq-final-reassurance__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <rect x="4" y="5.5" width="16" height="14" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.8"></rect>
            <path d="M8 3.5v4M16 3.5v4M4 9.5h16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
            <path d="M8.5 15a4 4 0 0 1 6.6-3M15.5 10.5v3h-3" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </span>
        <strong>Manage or reschedule online</strong>
      </div>

      <div class="seq-final-reassurance__item">
        <span class="seq-final-reassurance__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M5 8.5A8 8 0 1 1 4.7 15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
            <path d="M5 4.5v4h4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
            <path d="M12 8v8M14.5 10c0-1.1-1.1-1.8-2.5-1.8S9.5 8.9 9.5 10s1.1 1.8 2.5 1.8 2.5.7 2.5 1.8-1.1 1.8-2.5 1.8-2.5-.7-2.5-1.8" fill="none" stroke="currentColor" stroke-width="1.55" stroke-linecap="round"></path>
          </svg>
        </span>
        <strong>7-day money-back guarantee</strong>
      </div>

      <div class="seq-final-reassurance__item">
        <span class="seq-final-reassurance__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M12 3l7 3v5c0 4.5-2.8 8.3-7 10-4.2-1.7-7-5.5-7-10V6l7-3z" fill="none" stroke="currentColor" stroke-width="1.8"></path>
            <path d="M8.5 12.2l2.2 2.2 4.8-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </span>
        <strong>Fully insured \u00B7 owner-operated</strong>
      </div>
    `;
  }

  function configureBookingJourneyLanguage() {
    configureStepOneAppointmentAction();
    configureConfirmHeroIcon();
    configureFinalReassurance();

    const modalTitle = containingBookingDialog?.querySelector(
      '.seq-global-booking__header strong'
    );
    const modalSubheading = containingBookingDialog?.querySelector(
      '.seq-global-booking__subheading'
    );
    if (modalTitle) modalTitle.textContent = 'Book Your Split System Deep Clean';
    if (modalSubheading) {
      modalSubheading.textContent =
        'Choose your units, see real appointment times and pay after the service.';
    }

    const journeyCopy = [
      {
        panel: '1',
        eyebrow: 'Your clean',
        title: 'How many indoor split systems?',
        text: `${formatMoney(firstUnitPrice)} for the first indoor unit and ${formatMoney(additionalUnitPrice)} for each additional indoor unit at the same home.`
      },
      {
        panel: '2',
        eyebrow: 'Your appointment',
        title: 'Choose your appointment',
        text: 'Enter your suburb to see my next real appointment dates and times.'
      },
      {
        panel: '3',
        eyebrow: 'Last step',
        title: 'Confirm your appointment',
        text: 'Check your details. No card is required and nothing is charged today.'
      }
    ];

    journeyCopy.forEach(item => {
      const intro = el(`[data-step-panel="${item.panel}"] .seq-step-intro`);
      const eyebrow = intro?.querySelector(':scope > span');
      const title = intro?.querySelector('h3');
      const text = intro?.querySelector('p');
      if (eyebrow) eyebrow.textContent = item.eyebrow;
      if (title) title.textContent = item.title;
      if (text) text.textContent = item.text;
    });

    ensureActionReassurance(
      confirmNextButton,
      'data-step-two-payment-reassurance',
      'No upfront payment \u00B7 Pay after the clean'
    );

    section
      .querySelectorAll('[data-step-three-next-reassurance]')
      .forEach(node => node.remove());

    if (checkoutNoteEl) {
      checkoutNoteEl.textContent =
        'No card required. Pay after the service.';
    }

    if (ctaSecurityEl) {
      ctaSecurityEl.textContent =
        'Jordan personally completes your clean \u00B7 Fully insured \u00B7 Before & after proof';
    }

    const stepThreeIntro = stepThreePanel?.querySelector('.seq-step-intro p');
    if (stepThreeIntro) {
      stepThreeIntro.textContent =
        'Review your clean, appointment and details. Nothing is charged today.';
    }

    const confirmHero = stepThreePanel?.querySelector('.seq-confirm-hero div');
    if (confirmHero) {
      confirmHero.innerHTML =
        '<strong>Your appointment is held</strong><span>Check your details below to confirm it.</span>';
    }

    if (holdHelpEl) {
      holdHelpEl.textContent =
        'You can still change your date or time before confirming.';
    }
  }

  function configureStepOneAppointmentAction() {
    if (!stepOneAppointmentButton) return;

    stepOneAppointmentButton.textContent = 'See Available Times';
    stepOneAppointmentButton.setAttribute(
      'aria-label',
      'See my next available appointment dates and times'
    );

    const primaryWrapper = stepOneAppointmentButton.closest(
      '.seq-booking-sticky-actions__primary'
    );
    if (!primaryWrapper) return;

    let reassurance = primaryWrapper.querySelector(
      '[data-step-one-payment-reassurance]'
    );

    if (!reassurance) {
      reassurance = document.createElement('small');
      reassurance.className =
        'seq-booking-sticky-actions__reassurance';
      reassurance.setAttribute(
        'data-step-one-payment-reassurance',
        ''
      );
      reassurance.textContent =
        'No upfront payment \u00B7 Pay after the clean';
      primaryWrapper.appendChild(reassurance);
    }
  }

  function scrollBookingToTop(behavior = 'smooth') {
    if (containingBookingDialog) {
      if (!containingBookingDialog.open) return;
      const modalScroll = containingBookingDialog.querySelector('[data-seq-booking-scroll]');
      const activePanel = section.querySelector('.seq-step-panel.is-active');
      const progress = section.querySelector('[data-typeform-progress]');
      const target = activePanel || progress || section;
      if (!modalScroll || !target) return;

      const scrollRect = modalScroll.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const top = modalScroll.scrollTop + targetRect.top - scrollRect.top - 12;
      modalScroll.scrollTo({ top: Math.max(0, top), behavior });
      return;
    }

    section.querySelector('[data-seq-pdp-booking]')?.scrollIntoView({ behavior, block: 'start' });
  }

  const baseVariantId = Number(section.dataset.baseVariantId || 0);
  const firstUnitPrice = Number(section.dataset.firstUnitPrice || 14900);
  const additionalUnitPrice = Number(section.dataset.additionalUnitPrice || 9900);
  const outdoorVariantId = Number(section.dataset.outdoorVariantId || 0);
  const outdoorPrice = Number(section.dataset.outdoorPrice || 4900);

  let units = 1;
  let outdoorUnits = 0;
  let currentStep = 1;
  let selectedDate = null;
  let selectedTime = null;
  let selectedEndTime = null;
  let selectedBookingId = null;
  let manageBookingToken = null;
  let mobileAttempted = false;
  let detailsAttempted = false;
  let bookingSubmissionInFlight = false;
  let dateScrollerNudged = false;
  let availableDatesRequestId = 0;
  let availableTimesRequestId = 0;
  let cartTokenPromise = null;
  let reservationRequestId = 0;
  let reservationExpiresAt = null;
  let reservationCountdownTimer = null;
  let reservationInFlight = false;
  // WINTER now gives 100% off every selected outdoor-unit add-on.
  const winterOutdoorDiscountPercent = 100;
  const winterDiscountCode = 'WINTER';
  const offerStorageKey = 'seq_booking_offer_state_v2';
  const offerSessionSeenKey = 'seq_booking_offer_seen_session_v3';
  const offerResponseStorageKey = 'seq_booking_offer_response_session_v3';
  let winterSpecialApplied = false;
  let bookingStarted = false;
  let offerIsOpen = false;
  let appointmentSelectorOpened = false;
  let inlineOfferEl = null;
  let returningCustomerProfile = null;
  let returningCustomerBannerEl = null;
  let suburbSearchInput = null;
  let dateShowMoreButton = null;
  let experienceLayoutEl = null;
  let bookingContext = {};
  const trackedFunnelMilestones = new Set();

  function isRebookingContext() {
    return ['1', 'true', 'yes'].includes(String(bookingContext.rebook || '').toLowerCase());
  }

  function trackFunnel(eventName, details = {}) {
    window.SEQBookingFirstPartyTracking?.track?.(eventName, {
      step: currentStep,
      suburb: suburbSelect?.value || '',
      indoorUnits: units,
      outdoorUnits,
      valueCents: totalPrice(),
      discountCode: winterSpecialApplied ? winterDiscountCode : '',
      bookingId: selectedBookingId || null,
      isTest: internalTestMode,
      testReason: window.SEQBookingInternalTest?.reason || '',
      returningCustomer: Boolean(returningCustomerProfile),
      rebooking: isRebookingContext(),
      ...details,
      metadata: {
        selected_date: selectedDate || '',
        selected_time: selectedTime || '',
        returning_customer: Boolean(returningCustomerProfile) ? 'true' : 'false',
        rebooking: isRebookingContext() ? 'true' : 'false',
        internal_test: internalTestMode ? 'true' : 'false',
        test_reason: window.SEQBookingInternalTest?.reason || '',
        ...(details.metadata || {})
      }
    });
  }

  function trackFunnelOnce(key, eventName, details = {}) {
    if (trackedFunnelMilestones.has(key)) return;
    trackedFunnelMilestones.add(key);
    trackFunnel(eventName, details);
  }

  function readStoredJson(key) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeStoredJson(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Personalisation is optional when storage is unavailable.
    }
  }

  function readLocalStorageValue(key) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeLocalStorageValue(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function incomingPersonalisationContext() {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash || '';
    const hashQueryIndex = hash.indexOf('?');
    if (hashQueryIndex !== -1) {
      const hashParams = new URLSearchParams(hash.slice(hashQueryIndex + 1));
      hashParams.forEach((value, key) => {
        if (!params.has(key)) params.set(key, value);
      });
    }

    return {
      suburb: (params.get('suburb') || params.get('customer_suburb') || '').trim(),
      campaign: (params.get('campaign') || params.get('utm_campaign') || '').trim(),
      discount: (params.get('discount') || params.get('promo') || params.get('code') || '').trim(),
      rebook: params.get('rebook') || '',
      customerName: '',
      customerMobile: '',
      serviceAddress: ''
    };
  }

  function isWinterCampaignContext(context = {}) {
    const discount = String(context.discount || '').trim().toUpperCase();
    const campaign = String(context.campaign || '').trim().toLowerCase();
    return discount === winterDiscountCode || campaign.includes('winter');
  }

  function matchingSuburbOption(value) {
    if (!suburbSelect) return null;

    const raw = String(value || '').trim();
    if (!raw) return null;

    const suburbPart = raw.split(/\s+[\u2014\u2013-]\s+/)[0].trim().toLowerCase();
    const exactSuburb = Array.from(suburbSelect.options || []).find(option =>
      option.value.trim().toLowerCase() === suburbPart
    );
    if (exactSuburb) return exactSuburb;

    if (/^\d{4}$/.test(raw)) {
      const postcodeMatches = Array.from(suburbSelect.options || []).filter(option =>
        suburbPostcodes[option.value] === raw
      );
      return postcodeMatches.length === 1 ? postcodeMatches[0] : null;
    }

    return null;
  }

  function loadReturningCustomerProfile() {
    const profile = readStoredJson(customerProfileStorageKey);
    if (!profile || typeof profile !== 'object') return null;
    const savedAt = Number(profile.savedAt || 0);
    if (savedAt && Date.now() - savedAt > 1000 * 60 * 60 * 24 * 730) return null;
    return profile;
  }

  function saveReturningCustomerProfile(booking = {}) {
    const name = cleanCustomerName(customerNameInput?.value || booking.customerName || '');
    const mobile = normalizeAustralianMobile(mobileInput?.value || booking.customerMobile || '');
    const address = cleanServiceAddress(serviceAddressInput?.value || booking.address || '');
    if (!name || !mobile) return;

    writeStoredJson(customerProfileStorageKey, {
      name,
      mobile,
      address,
      suburb: booking.suburb || suburbSelect?.value || '',
      indoorUnits: Number(booking.indoorUnits || units || 1),
      outdoorUnits: Number(booking.outdoorUnits ?? outdoorUnits ?? 0),
      savedAt: Date.now()
    });
  }

  function applyCustomerDetails(context = {}, { allowProfile = true } = {}) {
    const profile = allowProfile ? returningCustomerProfile : null;
    const name = String(context.customerName || profile?.name || '').trim();
    const mobile = String(context.customerMobile || profile?.mobile || '').trim();
    const address = String(context.serviceAddress || profile?.address || '').trim();

    if (customerNameInput && !customerNameInput.value && name) customerNameInput.value = name;
    if (mobileInput && !mobileInput.value && mobile) mobileInput.value = formatAustralianMobileDisplay(mobile);
    if (serviceAddressInput && !serviceAddressInput.value && address) serviceAddressInput.value = address;
  }

  function applyPersonalisationContext(context = {}, { initial = false } = {}) {
    bookingContext = { ...bookingContext, ...context };
    applyCustomerDetails(context);

    const profileSuburb = returningCustomerProfile?.suburb || '';
    const requestedSuburb = String(context.suburb || (!initial ? '' : profileSuburb)).trim();
    const suburbOption = matchingSuburbOption(requestedSuburb);
    if (suburbOption && suburbSelect && suburbSelect.value !== suburbOption.value) {
      suburbSelect.value = suburbOption.value;
      if (!initial) suburbSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (isWinterCampaignContext(context) && !winterSpecialApplied) {
      applyWinterSpecial({ source: 'campaign', scroll: false, celebrate: false });
    }

    const isRebook = ['1', 'true', 'yes'].includes(String(context.rebook || '').toLowerCase());
    section.classList.toggle('is-rebooking', isRebook);
    ensureReturningCustomerBanner({ rebook: isRebook });
    updateInlineOffer();
    updateExperienceSummaries();
    queueBookingDraftSave();
  }

  function getPreselectedQuantities() {
    const params = new URLSearchParams(window.location.search);

    // Also support parameters placed after the anchor:
    // #seq-pdp-book?units=3&outdoor=1
    const hash = window.location.hash || '';
    const hashQueryIndex = hash.indexOf('?');
    if (hashQueryIndex !== -1) {
      const hashParams = new URLSearchParams(hash.slice(hashQueryIndex + 1));
      hashParams.forEach((value, key) => {
        if (!params.has(key)) params.set(key, value);
      });
    }

    const hasIndoorOverride =
      params.has('units') ||
      params.has('indoor') ||
      params.has('indoor_units');
    const hasOutdoorOverride =
      params.has('outdoor') ||
      params.has('outdoor_units');

    if (!hasIndoorOverride && !hasOutdoorOverride) return false;

    const requestedUnits = Number.parseInt(
      params.get('units') ||
      params.get('indoor') ||
      params.get('indoor_units') ||
      String(units),
      10
    );
    const requestedOutdoor = Number.parseInt(
      params.get('outdoor') ||
      params.get('outdoor_units') ||
      String(outdoorUnits),
      10
    );

    units = Number.isFinite(requestedUnits)
      ? Math.max(1, Math.min(10, requestedUnits))
      : units;

    outdoorUnits = Number.isFinite(requestedOutdoor)
      ? Math.max(0, Math.min(units, requestedOutdoor))
      : Math.min(outdoorUnits, units);

    return true;
  }

  const bookingDraftStorageKey = 'seq_booking_draft_v1';
  const bookingDraftMaxAgeMs = 7 * 24 * 60 * 60 * 1000;
  let bookingDraftSaveTimer = null;
  let isRestoringBookingDraft = false;
  let bookingDraftCompleted = false;

  function parseStoredDate(value) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))
      ? String(value)
      : null;
  }

  function normalizeStoredTime(value) {
    const match = String(value || '').match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}:00` : null;
  }

  function hasActiveReservation() {
    return Boolean(
      selectedBookingId &&
      manageBookingToken &&
      selectedDate &&
      selectedTime &&
      reservationExpiresAt instanceof Date &&
      !Number.isNaN(reservationExpiresAt.getTime()) &&
      reservationExpiresAt.getTime() > Date.now()
    );
  }

  function readBookingDraft() {
    try {
      const raw = localStorage.getItem(bookingDraftStorageKey);
      if (!raw) return null;

      const draft = JSON.parse(raw);
      const savedAt = Number(draft?.savedAt || 0);

      if (
        draft?.version !== 1 ||
        !savedAt ||
        Date.now() - savedAt > bookingDraftMaxAgeMs
      ) {
        localStorage.removeItem(bookingDraftStorageKey);
        return null;
      }

      return draft;
    } catch (error) {
      console.warn('Could not read saved booking progress.', error);
      return null;
    }
  }

  function clearBookingDraft() {
    if (bookingDraftSaveTimer) {
      window.clearTimeout(bookingDraftSaveTimer);
      bookingDraftSaveTimer = null;
    }

    try {
      localStorage.removeItem(bookingDraftStorageKey);
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  }

  function saveBookingDraft() {
    if (isRestoringBookingDraft || bookingDraftCompleted) return;

    const suburb = suburbSelect?.value || '';
    const mobile = mobileInput?.value?.trim() || '';
    const customerName = customerNameInput?.value?.trim() || '';
    const serviceAddress = serviceAddressInput?.value?.trim() || '';
    const hasMeaningfulProgress =
      units !== 1 ||
      outdoorUnits !== 0 ||
      currentStep > 1 ||
      Boolean(suburb) ||
      Boolean(selectedDate) ||
      Boolean(selectedBookingId) ||
      Boolean(mobile) ||
      Boolean(customerName) ||
      Boolean(serviceAddress) ||
      winterSpecialApplied;

    if (!hasMeaningfulProgress) {
      clearBookingDraft();
      return;
    }

    const draft = {
      version: 1,
      savedAt: Date.now(),
      units,
      outdoorUnits,
      currentStep,
      suburb,
      selectedDate,
      selectedTime,
      selectedEndTime,
      selectedBookingId,
      manageBookingToken,
      reservationExpiresAt:
        reservationExpiresAt instanceof Date &&
        !Number.isNaN(reservationExpiresAt.getTime())
          ? reservationExpiresAt.toISOString()
          : null,
      mobile,
      customerName,
      serviceAddress
    };

    try {
      localStorage.setItem(bookingDraftStorageKey, JSON.stringify(draft));
    } catch (error) {
      console.warn('Could not save booking progress.', error);
    }
  }

  function queueBookingDraftSave() {
    if (isRestoringBookingDraft) return;
    if (bookingDraftSaveTimer) window.clearTimeout(bookingDraftSaveTimer);
    bookingDraftSaveTimer = window.setTimeout(() => {
      bookingDraftSaveTimer = null;
      saveBookingDraft();
    }, 100);
  }

  function restoreBookingDraft() {
    const draft = readBookingDraft();
    if (!draft) return null;

    isRestoringBookingDraft = true;

    units = Math.max(1, Math.min(10, Number(draft.units) || 1));
    outdoorUnits = Math.max(
      0,
      Math.min(units, Number(draft.outdoorUnits) || 0)
    );

    const storedSuburb = String(draft.suburb || '').trim();
    const matchingSuburb = Array.from(suburbSelect?.options || []).find(
      option => option.value.toLowerCase() === storedSuburb.toLowerCase()
    );
    if (suburbSelect && matchingSuburb) {
      suburbSelect.value = matchingSuburb.value;
    }

    if (mobileInput && typeof draft.mobile === 'string') {
      mobileInput.value = formatAustralianMobileDisplay(draft.mobile);
    }
    if (customerNameInput && typeof draft.customerName === 'string') {
      customerNameInput.value = draft.customerName.slice(0, 100);
    }
    if (serviceAddressInput && typeof draft.serviceAddress === 'string') {
      serviceAddressInput.value = draft.serviceAddress.slice(0, 180);
    }

    selectedDate = parseStoredDate(draft.selectedDate);
    selectedTime = normalizeStoredTime(draft.selectedTime);
    selectedEndTime = normalizeStoredTime(draft.selectedEndTime);
    selectedBookingId = draft.selectedBookingId || null;
    manageBookingToken = draft.manageBookingToken || null;

    const storedExpiry = draft.reservationExpiresAt
      ? new Date(draft.reservationExpiresAt)
      : null;
    reservationExpiresAt =
      storedExpiry && !Number.isNaN(storedExpiry.getTime())
        ? storedExpiry
        : null;

    if (!hasActiveReservation()) {
      selectedTime = null;
      selectedEndTime = null;
      selectedBookingId = null;
      manageBookingToken = null;
      reservationExpiresAt = null;
    }

    currentStep = Math.max(1, Math.min(3, Number(draft.currentStep) || 1));
    if (!suburbSelect?.value) currentStep = 1;
    else if (currentStep === 3 && !selectedBookingId) currentStep = 2;

    bookingStarted = true;
    appointmentSelectorOpened =
      currentStep >= 2 || Boolean(selectedDate) || Boolean(selectedBookingId);

    isRestoringBookingDraft = false;

    return {
      restored: true,
      activeReservation: hasActiveReservation(),
      selectedDate
    };
  }

  function restoreReservationPresentation() {
    if (!hasActiveReservation()) return;

    if (bookingSelectedTextEl) {
      bookingSelectedTextEl.textContent =
        `${formatDateFromISO(selectedDate)} at ${formatTime(selectedTime)}`;
    }

    if (bookingSelectedEl) {
      bookingSelectedEl.hidden = false;
      bookingSelectedEl.classList.remove('is-expired');
    }

    if (progressAppointmentEl) {
      progressAppointmentEl.textContent =
        `${formatDateFromISO(selectedDate)} \u00B7 ${formatTime(selectedTime)}`;
    }

    startReservationCountdown(reservationExpiresAt);
    updateAppointmentGuidance();
    updateReview();
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency', currency: 'AUD', minimumFractionDigits: 0
    }).format(cents / 100);
  }

  function formatAverageMoney(cents) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: Number.isInteger(cents / 100) ? 0 : 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
  }

  function ensureWinterValueExperience() {
    if (winterValuePanelEl && winterInlinePriceEl && winterIndoorRowEl) {
      return;
    }

    winterIndoorRowEl =
      el('[data-action="minus"]')?.closest('.seq-unit-row')
      || el('[data-unit-count]')?.closest('.seq-unit-row')
      || null;

    if (!winterIndoorRowEl) return;

    const indoorCopy =
      winterIndoorRowEl.querySelector('.seq-unit-row__copy')
      || winterIndoorRowEl.querySelector('article, div');

    if (!winterInlinePriceEl && indoorCopy) {
      winterInlinePriceEl = document.createElement('div');
      winterInlinePriceEl.className = 'seq-winter-inline-price';
      winterInlinePriceEl.hidden = true;
      winterInlinePriceEl.setAttribute('data-winter-inline-price', '');
      winterInlinePriceEl.innerHTML = `
        <div class="seq-winter-inline-price__group seq-winter-inline-price__group--first">
          <small>Outdoor unit cleaning</small>
          <div class="seq-winter-inline-price__amounts">
            <s data-winter-inline-usual></s>
            <strong data-winter-inline-now></strong>
          </div>
        </div>
        <div class="seq-winter-inline-price__group seq-winter-inline-price__group--extra">
          <small>WINTER</small>
          <div class="seq-winter-inline-price__amounts">
            <strong data-winter-inline-extra></strong>
            <span>all selected outdoor units</span>
          </div>
        </div>
      `;
      indoorCopy.appendChild(winterInlinePriceEl);
    }

    if (!winterValuePanelEl) {
      winterValuePanelEl = document.createElement('section');
      winterValuePanelEl.className = 'seq-winter-value-panel';
      winterValuePanelEl.hidden = true;
      winterValuePanelEl.setAttribute('data-winter-value-panel', '');
      winterValuePanelEl.setAttribute('aria-live', 'polite');
      winterValuePanelEl.innerHTML = `
        <div class="seq-winter-value-panel__header">
          <div class="seq-winter-value-panel__identity">
            <span class="seq-winter-value-panel__icon" aria-hidden="true">\u2744</span>
            <div>
              <small>WINTER ACTIVE \u00B7 LIMITED TIME</small>
              <strong>Your free outdoor unit clean is locked in</strong>
            </div>
          </div>
          <span class="seq-winter-value-panel__discount">FREE OUTDOOR CLEAN</span>
        </div>

        <div class="seq-winter-value-panel__stats">
          <div class="seq-winter-value-panel__stat seq-winter-value-panel__stat--primary">
            <small>Your indoor total</small>
            <strong data-winter-indoor-total></strong>
          </div>
          <div class="seq-winter-value-panel__stat">
            <small>Average per indoor unit</small>
            <strong data-winter-average-price></strong>
          </div>
        </div>

        <p class="seq-winter-value-panel__home-value">
          <span class="seq-winter-value-panel__home-value-icon" aria-hidden="true">\u2744</span>
          <span class="seq-winter-value-panel__home-value-copy" data-winter-home-value></span>
        </p>

        <small class="seq-winter-value-panel__footnote">
          Limited time: WINTER gives 100% off every outdoor-unit clean added to this booking.
        </small>
      `;

      winterIndoorRowEl.insertAdjacentElement('afterend', winterValuePanelEl);
    }

    section.classList.add('has-winter-value-panel');
  }

  function updateWinterValueExperience() {
    ensureWinterValueExperience();
    if (!winterValuePanelEl || !winterInlinePriceEl || !winterIndoorRowEl) return;

    const currentIndoorTotal = indoorTotal();
    const averageIndoorPrice = units > 0
      ? currentIndoorTotal / units
      : currentIndoorTotal;

    winterInlinePriceEl.hidden = !winterSpecialApplied;
    winterValuePanelEl.hidden = false;

    winterIndoorRowEl.classList.toggle(
      'has-winter-price',
      winterSpecialApplied
    );

    winterValuePanelEl.classList.toggle(
      'is-promo',
      winterSpecialApplied
    );
    winterValuePanelEl.classList.toggle(
      'is-standard',
      !winterSpecialApplied
    );

    section.classList.toggle(
      'has-active-winter-value',
      winterSpecialApplied
    );
    section.classList.toggle(
      'has-standard-booking-value',
      !winterSpecialApplied
    );

    const setText = (selector, value) => {
      const target =
        winterValuePanelEl.querySelector(selector)
        || winterInlinePriceEl.querySelector(selector);
      if (target) target.textContent = value;
    };

    if (winterSpecialApplied) {
      setText('[data-winter-inline-usual]', `${formatMoney(outdoorPrice)} ea`);
      setText('[data-winter-inline-now]', 'FREE');
      setText('[data-winter-inline-extra]', '100% off');
    }

    setText('[data-winter-indoor-total]', formatMoney(currentIndoorTotal));
    setText(
      '[data-winter-average-price]',
      `${formatAverageMoney(averageIndoorPrice)} each`
    );
    const homeValueEl = winterValuePanelEl.querySelector(
      '[data-winter-home-value]'
    );

    if (homeValueEl) {
      if (winterSpecialApplied) {
        if (units === 1) {
          homeValueEl.innerHTML = `Get your split system professionally deep cleaned for <strong>${formatMoney(currentIndoorTotal)}</strong>. Limited time: add its outdoor unit clean <strong>FREE</strong> with WINTER.`;
        } else {
          homeValueEl.innerHTML = `Get all ${units} indoor units professionally deep cleaned for <strong>${formatMoney(currentIndoorTotal)}</strong> \u2014 just <strong>${formatAverageMoney(averageIndoorPrice)} per unit</strong>. Limited time: every selected outdoor unit clean is <strong>FREE</strong> with WINTER.`;
        }
      } else if (units === 1) {
        homeValueEl.innerHTML = `Get your split system professionally deep cleaned for <strong>${formatMoney(currentIndoorTotal)}</strong>.`;
      } else {
        homeValueEl.innerHTML = `Get all ${units} indoor units professionally deep cleaned for <strong>${formatMoney(currentIndoorTotal)}</strong> \u2014 <strong>${formatAverageMoney(averageIndoorPrice)} per unit</strong>.`;
      }
    }
  }

  function celebrateWinterValueExperience() {
    if (!inlineOfferEl) return;

    if (winterValueApplyTimer) {
      window.clearTimeout(winterValueApplyTimer);
    }

    inlineOfferEl.classList.remove('is-just-applied');
    window.requestAnimationFrame(() => {
      inlineOfferEl.classList.add('is-just-applied');
    });

    winterValueApplyTimer = window.setTimeout(() => {
      inlineOfferEl?.classList.remove('is-just-applied');
      winterValueApplyTimer = null;
    }, 900);
  }

  function indoorTotal() {
    return firstUnitPrice + Math.max(0, units - 1) * additionalUnitPrice;
  }

  function totalPrice() {
    const outdoorDiscountMultiplier = winterSpecialApplied
      ? Math.max(0, 1 - winterOutdoorDiscountPercent / 100)
      : 1;
    const outdoorTotal = outdoorUnits * outdoorPrice * outdoorDiscountMultiplier;
    return indoorTotal() + outdoorTotal;
  }

  function getDurationMinutes() {
    return units * 60 + outdoorUnits * 15;
  }

  function plural(quantity, singular) {
    return `${quantity} ${singular}${quantity === 1 ? '' : 's'}`;
  }

  function updatePricing() {
    const formatted = formatMoney(totalPrice());

    if (firstUnitDisplayEl) {
      firstUnitDisplayEl.textContent = formatMoney(
        firstUnitPrice
      );
    }

    if (winterAppliedEl) winterAppliedEl.hidden = true;

    if (mobilePriceEl) {
      mobilePriceEl.textContent = winterSpecialApplied
        ? `From ${formatMoney(firstUnitPrice)} \u00B7 Outdoor clean FREE`
        : `From ${formatMoney(firstUnitPrice)} \u00B7 Pay after`;
    }

    if (totalPriceEl) totalPriceEl.textContent = formatted;
    if (summaryTotalEl) summaryTotalEl.textContent = formatted;
    if (unitCountEl) unitCountEl.textContent = units;
    if (outdoorCountEl) outdoorCountEl.textContent = outdoorUnits;

    removeLegacyWinterOfferContainers();
    updateStepOneConversionProof();

    if (savingEl) {
      if (winterSpecialApplied) {
        savingEl.hidden = true;
        savingEl.textContent = '';
      } else {
        const saving =
          Math.max(0, units - 1)
          * Math.max(0, firstUnitPrice - additionalUnitPrice);

        savingEl.hidden = saving <= 0;
        savingEl.textContent = saving > 0
          ? `Multi-unit pricing saves you ${formatMoney(saving)} versus separate first-unit visits.`
          : '';
      }
    }

    const parts = [plural(units, 'indoor unit')];
    if (outdoorUnits > 0) {
      parts.push(plural(outdoorUnits, 'outdoor unit'));
    }
    if (winterSpecialApplied) {
      parts.push(outdoorUnits > 0 ? 'Outdoor cleaning FREE' : 'WINTER applied');
    } else {
      parts.push('Pay after service');
    }

    if (summaryDetailEl) {
      summaryDetailEl.textContent = parts.join(' \u00B7 ');
    }

    const reviewIndoor = el('[data-review-indoor]');
    const reviewOutdoor = el('[data-review-outdoor]');
    const reviewOutdoorRow = el('[data-review-outdoor-row]');
    const reviewTotal = el('[data-review-total]');

    if (reviewIndoor) reviewIndoor.textContent = String(units);
    if (reviewOutdoor) reviewOutdoor.textContent = String(outdoorUnits);
    if (reviewOutdoorRow) reviewOutdoorRow.hidden = outdoorUnits < 1;
    if (reviewTotal) reviewTotal.textContent = formatted;

    // Value framing is display-only. It derives from the same pricing values
    // used by totalPrice(), so it cannot alter what the customer is charged.
    const currentIndoorTotal = indoorTotal();
    const averageIndoorPrice = units > 0 ? currentIndoorTotal / units : currentIndoorTotal;
    const outdoorUsualValue = outdoorUnits * outdoorPrice;
    const winterOutdoorSaving = winterSpecialApplied ? outdoorUsualValue : 0;
    const valueUnitCount = el('[data-value-unit-count]');
    const valueUnitLabel = el('[data-value-unit-label]');
    const valueIndoorLabel = el('[data-value-indoor-label]');
    const valueIndoorTotal = el('[data-value-indoor-total]');
    const valueOutdoorRow = el('[data-value-outdoor-row]');
    const valueOutdoorLabel = el('[data-value-outdoor-label]');
    const valueOutdoorUsual = el('[data-value-outdoor-usual]');
    const valueOutdoorPrice = el('[data-value-outdoor-price]');
    const valueSavingRow = el('[data-value-saving-row]');
    const valueSaving = el('[data-value-saving]');
    const valueTotal = el('[data-value-total]');
    const valuePerUnit = el('[data-value-per-unit]');

    if (valueUnitCount) valueUnitCount.textContent = String(units);
    if (valueUnitLabel) valueUnitLabel.textContent = units === 1 ? 'split system deep clean' : 'split system deep cleans';
    if (valueIndoorLabel) valueIndoorLabel.textContent = units === 1 ? '1 indoor deep clean' : `${units} indoor deep cleans`;
    if (valueIndoorTotal) valueIndoorTotal.textContent = formatMoney(currentIndoorTotal);
    if (valueTotal) valueTotal.textContent = formatted;
    if (valuePerUnit) {
      valuePerUnit.textContent = units > 1
        ? `${formatAverageMoney(averageIndoorPrice)} per indoor unit \u00B7 Pay after service`
        : 'No upfront payment \u00B7 Pay after service';
    }

    if (valueOutdoorRow) valueOutdoorRow.hidden = outdoorUnits < 1;
    if (valueOutdoorLabel) {
      valueOutdoorLabel.textContent = outdoorUnits === 1
        ? '1 outdoor unit clean'
        : `${outdoorUnits} outdoor unit cleans`;
    }
    if (valueOutdoorUsual) {
      valueOutdoorUsual.hidden = !(winterSpecialApplied && outdoorUnits > 0);
      valueOutdoorUsual.textContent = formatMoney(outdoorUsualValue);
    }
    if (valueOutdoorPrice) {
      valueOutdoorPrice.textContent = winterSpecialApplied && outdoorUnits > 0
        ? 'FREE'
        : formatMoney(outdoorUsualValue);
      valueOutdoorPrice.classList.toggle('is-free', winterSpecialApplied && outdoorUnits > 0);
    }
    if (valueSavingRow) valueSavingRow.hidden = winterOutdoorSaving <= 0;
    if (valueSaving) valueSaving.textContent = formatMoney(winterOutdoorSaving);

    if (checkoutNoteEl) {
      checkoutNoteEl.textContent = `You'll pay ${formatted} after the service is complete.`;
    }

    if (progressUnitsEl) {
      const unitParts = [plural(units, 'indoor')];
      if (outdoorUnits > 0) {
        unitParts.push(plural(outdoorUnits, 'outdoor'));
      }
      progressUnitsEl.textContent = unitParts.join(' + ');
    }
    updateInlineOffer();
    updateExperienceSummaries();
  }

  function pushOfferEvent(eventName, details = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...details });
  }

  function setOfferView(name) {
    offerEls('[data-offer-view]').forEach(view => {
      view.classList.toggle('is-active', view.dataset.offerView === name);
    });
  }

  function pulseHaptic(duration = 12) {
    try {
      if ('vibrate' in navigator) navigator.vibrate(duration);
    } catch (error) {
      // Vibration is optional and unsupported on many devices.
    }
  }

  function readSessionValue(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeSessionValue(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      // Session storage can be unavailable in privacy-restricted browsers.
    }
  }

  function removeSessionValue(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      // Session storage can be unavailable in privacy-restricted browsers.
    }
  }

  function openOfferSheet({ force = false, trigger = 'manual' } = {}) {
    if (!offerSheet || !offerBackdrop || !canShowOffer({ force })) return false;
    offerSheet.hidden = false;
    offerBackdrop.hidden = false;
    setOfferView('discount');
    offerIsOpen = true;
    offerSheet.setAttribute('aria-hidden', 'false');
    window.requestAnimationFrame(() => {
      offerSheet.classList.add('is-open');
      window.setTimeout(() => {
        offerEl('[data-offer-apply]')?.focus({ preventScroll: true });
      }, 120);
    });
    document.documentElement.classList.add('seq-offer-open');
    containingBookingDialog?.classList.add('seq-offer-open');
    writeSessionValue(offerSessionSeenKey, String(Date.now()));
    pushOfferEvent('seq_winter_offer_shown', {
      discount_code: winterDiscountCode,
      discount_value: outdoorUnits * outdoorPrice / 100,
      trigger,
      booking_modal_open: Boolean(containingBookingDialog?.open)
    });
    return true;
  }

  function closeOfferSheet({ closeAutoOpenedBooking = false } = {}) {
    if (!offerSheet || !offerBackdrop) return;
    offerSheet.classList.remove('is-open');
    offerSheet.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('seq-offer-open');
    containingBookingDialog?.classList.remove('seq-offer-open');
    offerIsOpen = false;
    window.setTimeout(() => {
      offerSheet.hidden = true;
      offerBackdrop.hidden = true;
      if (
        closeAutoOpenedBooking &&
        containingBookingDialog?.dataset.offerAutoOpened === 'true'
      ) {
        containingBookingDialog.dispatchEvent(
          new CustomEvent('seq:offer-auto-dismissed', { bubbles: false })
        );
      }
    }, 380);
  }

  function canShowOffer() {
    if (
      true ||
      promotionsDisabled ||
      winterSpecialApplied ||
      offerIsOpen ||
      document.hidden
    ) {
      return false;
    }

    return force || !readSessionValue(offerSessionSeenKey);
  }

  function markBookingStarted(action = 'interaction') {
    if (!bookingStarted) {
      bookingStarted = true;
      pushOfferEvent('seq_booking_started', { action });
    }
  }

  function saveOfferResponse(reason) {
    writeSessionValue(offerResponseStorageKey, JSON.stringify({
      reason,
      recorded_at: Date.now()
    }));
  }

  function applyWinterSpecial(options = {}) {
    const source = options?.source || (options?.type ? 'legacy_offer' : 'manual');
    const shouldScroll = options?.scroll !== false;
    const shouldCelebrate = options?.celebrate !== false;
    if (winterSpecialApplied) {
      updateInlineOffer();
      return;
    }
    pulseHaptic(18);
    winterSpecialApplied = true;
    writeLocalStorageValue(offerStorageKey, 'applied');
    if (containingBookingDialog) delete containingBookingDialog.dataset.offerAutoOpened;
    updatePricing();
    if (shouldCelebrate) celebrateWinterValueExperience();
    pushOfferEvent('seq_winter_offer_applied', { discount_code: winterDiscountCode, discount_value: outdoorUnits * outdoorPrice / 100, source });
    containingBookingDialog?.dispatchEvent(new CustomEvent('seq:offer-state-changed', {
      bubbles: false,
      detail: { applied: true }
    }));
    queueBookingDraftSave();
    closeOfferSheet();
    if (shouldScroll) scrollBookingToTop();
  }

  function updateDateScrollerState() {
    if (!bookingDatesEl) return;
    const shell = bookingDatesEl.closest('[data-date-scroll-shell]');
    if (!shell) return;

    const maxScroll = Math.max(0, bookingDatesEl.scrollWidth - bookingDatesEl.clientWidth);
    shell.classList.toggle('is-at-end', maxScroll <= 2 || bookingDatesEl.scrollLeft >= maxScroll - 2);
  }

  function nudgeDateScroller() {
    if (
      dateScrollerNudged ||
      !bookingDatesEl ||
      !window.matchMedia('(max-width: 768px)').matches ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return;

    dateScrollerNudged = true;
    window.setTimeout(() => {
      if (bookingDatesEl.scrollWidth <= bookingDatesEl.clientWidth + 8) return;
      bookingDatesEl.classList.add('is-nudging');
      window.setTimeout(() => bookingDatesEl.classList.remove('is-nudging'), 760);
    }, 320);
  }

  function showStep(step) {
    currentStep = Math.max(1, Math.min(3, step));
    els('[data-step-panel]').forEach(panel => {
      panel.classList.toggle('is-active', Number(panel.dataset.stepPanel) === currentStep);
    });
    els('[data-step-jump]').forEach(button => {
      const buttonStep = Number(button.dataset.stepJump);
      button.classList.toggle('is-active', buttonStep === currentStep);
      button.classList.toggle('is-complete', buttonStep < currentStep);
      button.setAttribute('aria-current', buttonStep === currentStep ? 'step' : 'false');
    });

    if (progressAppointmentEl) {
      progressAppointmentEl.textContent = selectedDate && selectedTime
        ? `${formatDateFromISO(selectedDate)} \u00B7 ${formatTime(selectedTime)}`
        : selectedDate
          ? formatDateFromISO(selectedDate)
          : 'Choose a time';
    }
    updateReview();
    updateExperienceSummaries();
    pushOfferEvent('seq_booking_step_view', { step: currentStep });
    if (currentStep === 2) {
      trackFunnelOnce('appointment_step_opened', 'appointment_step_opened');
      window.requestAnimationFrame(() => {
        updateDateScrollerState();
        nudgeDateScroller();
      });
    }
    if (currentStep === 3) {
      trackFunnelOnce('details_step_opened', 'details_step_opened');
    }
    queueBookingDraftSave();
    scrollBookingToTop();
  }

  function toLocalISODate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDateFromISO(isoDate) {
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-AU', {
      weekday: 'short', day: 'numeric', month: 'short'
    });
  }

  function formatBookingDate(isoDate) {
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-AU', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  }

  function formatTime(time) {
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date.toLocaleTimeString('en-AU', {
      hour: 'numeric', minute: minutes === 0 ? undefined : '2-digit', hour12: true
    }).toLowerCase();
  }

  function calculateEndTime(startTime, durationMinutes) {
    const [hours, minutes] = startTime.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    date.setMinutes(date.getMinutes() + durationMinutes);
    return date.toTimeString().slice(0, 8);
  }

  function setBookingStepState(selector, state, statusText) {
    const element = el(selector);
    if (!element) return;
    element.classList.remove('is-current', 'is-complete', 'is-locked');
    element.classList.add(state);
    const status = element.querySelector('[data-step-status]');
    if (status) status.textContent = statusText;
  }

  function updateAppointmentGuidance() {
    const hasSuburb = Boolean(suburbSelect?.value);
    const hasDate = Boolean(selectedDate);
    const hasTime = Boolean(selectedBookingId);
    const title = el('[data-booking-guide-title]');
    const text = el('[data-booking-guide-text]');
    const timeStep = el('[data-booking-step="time"]');

    if (timeStep) timeStep.hidden = !hasDate;

    if (!hasSuburb) {
      setBookingStepState('[data-booking-step="suburb"]', 'is-current', 'Start here');
      setBookingStepState('[data-booking-step="date"]', 'is-locked', 'Next');
      setBookingStepState('[data-booking-step="time"]', 'is-locked', 'Last step');
      if (title) title.textContent = 'Enter your suburb or postcode';
      if (text) text.textContent = 'I\u2019ll show the next available appointments in your area.';
    } else if (!hasDate) {
      setBookingStepState('[data-booking-step="suburb"]', 'is-complete', 'Selected');
      setBookingStepState('[data-booking-step="date"]', 'is-current', 'Choose date');
      setBookingStepState('[data-booking-step="time"]', 'is-locked', 'Last step');
      if (title) title.textContent = 'Choose your appointment date';
      if (text) text.textContent = `Next available dates for ${suburbSelect.value}.`;
    } else if (!hasTime) {
      setBookingStepState('[data-booking-step="suburb"]', 'is-complete', 'Selected');
      setBookingStepState('[data-booking-step="date"]', 'is-complete', 'Selected');
      setBookingStepState('[data-booking-step="time"]', 'is-current', 'Choose time');
      if (title) title.textContent = 'Choose an arrival time';
      if (text) text.textContent = formatDateFromISO(selectedDate);
    } else {
      setBookingStepState('[data-booking-step="suburb"]', 'is-complete', 'Selected');
      setBookingStepState('[data-booking-step="date"]', 'is-complete', 'Selected');
      setBookingStepState('[data-booking-step="time"]', 'is-complete', 'Selected');
      if (title) title.textContent = 'Appointment selected';
      if (text) text.textContent = `${formatDateFromISO(selectedDate)} at ${formatTime(selectedTime)}.`;
    }

    if (confirmNextButton) {
      confirmNextButton.disabled = !hasTime;
      confirmNextButton.classList.toggle('is-disabled', !hasTime);
      confirmNextButton.textContent = !hasSuburb
        ? 'Enter your suburb first'
        : !hasDate
          ? 'Select a date to continue'
          : !hasTime
            ? 'Select an arrival time'
            : 'Review My Booking';
    }
    updateExperienceSummaries();
  }

  function stopReservationCountdown() {
    if (reservationCountdownTimer) {
      window.clearInterval(reservationCountdownTimer);
      reservationCountdownTimer = null;
    }
  }

  function formatCountdown(milliseconds) {
    const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  function updateHoldCardState(remainingMs) {
    if (!bookingSelectedEl) return;

    const isUrgent = remainingMs <= 2 * 60 * 1000;
    const isCritical = remainingMs <= 30 * 1000;

    bookingSelectedEl.classList.toggle('is-urgent', isUrgent);
    bookingSelectedEl.classList.toggle('is-critical', isCritical);

    if (holdTitleEl) {
      holdTitleEl.textContent = isUrgent
        ? 'Your appointment hold expires soon'
        : 'I\u2019m holding this appointment for 10 minutes';
    }

    if (holdHelpEl) {
      holdHelpEl.textContent = isUrgent
        ? 'Complete your booking now to keep this appointment.'
        : 'You can still change your date or time before confirming.';
    }
  }

  async function handleReservationExpiry() {
    stopReservationCountdown();

    reservationExpiresAt = null;
    selectedTime = null;
    selectedEndTime = null;
    selectedBookingId = null;
    manageBookingToken = null;

    if (bookingSelectedEl) {
      bookingSelectedEl.hidden = false;
      bookingSelectedEl.classList.remove('is-urgent', 'is-critical');
      bookingSelectedEl.classList.add('is-expired');
    }

    if (holdTitleEl) holdTitleEl.textContent = 'Your appointment hold has expired';
    if (holdCountdownEl) holdCountdownEl.textContent = '00:00';
    if (holdHelpEl) holdHelpEl.textContent = 'I\u2019m refreshing the available times for you.';

    if (progressAppointmentEl) {
      progressAppointmentEl.textContent = selectedDate
        ? formatDateFromISO(selectedDate)
        : 'Choose a time';
    }

    updateAppointmentGuidance();
    updateReview();
    queueBookingDraftSave();

    await fetchAvailableTimes();

    window.setTimeout(() => {
      if (bookingSelectedEl && !selectedBookingId) {
        bookingSelectedEl.hidden = true;
        bookingSelectedEl.classList.remove('is-expired');
      }
    }, 2200);
  }

  function startReservationCountdown(reservedUntil) {
    stopReservationCountdown();

    reservationExpiresAt = reservedUntil ? new Date(reservedUntil) : null;

    if (!reservationExpiresAt || Number.isNaN(reservationExpiresAt.getTime())) {
      reservationExpiresAt = new Date(Date.now() + 10 * 60 * 1000);
    }

    const tick = () => {
      const remainingMs = reservationExpiresAt.getTime() - Date.now();

      if (holdCountdownEl) {
        holdCountdownEl.textContent = formatCountdown(remainingMs);
      }

      updateHoldCardState(remainingMs);

      if (remainingMs <= 0) {
        handleReservationExpiry();
      }
    };

    tick();
    reservationCountdownTimer = window.setInterval(tick, 1000);
  }

  function resetReservation(refetch = true) {
    stopReservationCountdown();
    reservationExpiresAt = null;
    selectedTime = null;
    selectedEndTime = null;
    selectedBookingId = null;
    manageBookingToken = null;
    if (bookingSelectedEl) bookingSelectedEl.hidden = true;
    if (bookingSelectedTextEl) bookingSelectedTextEl.textContent = '';
    els('.seq-booking-time').forEach(button => button.classList.remove('is-active'));
    if (refetch && selectedDate && suburbSelect?.value) fetchAvailableTimes();
    updateAppointmentGuidance();
    updateReview();
    queueBookingDraftSave();
  }

  function renderDateLoadingState() {
    if (!bookingDatesEl) return;

    bookingDatesEl.setAttribute('aria-busy', 'true');
    bookingDatesEl.innerHTML = `
      <div class="seq-date-loading" role="status" aria-live="polite">
        <span class="seq-date-loading__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <rect x="4" y="5" width="16" height="15" rx="3" fill="none" stroke="currentColor" stroke-width="1.8"></rect>
            <path d="M8 3v4M16 3v4M4 9h16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
            <path d="M9 14l2 2 4-4" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </span>
        <div class="seq-date-loading__copy">
          <strong>Finding appointments in ${suburbSelect?.value || 'your suburb'}</strong>
          <span>Checking my next available route</span>
        </div>
        <span class="seq-date-loading__dots" aria-hidden="true"><i></i><i></i><i></i></span>
        <span class="seq-date-loading__progress" aria-hidden="true"><i></i></span>
      </div>
    `;
  }

  function revealAvailableDates() {
    if (!bookingDatesEl) return;

    bookingDatesEl.removeAttribute('aria-busy');
    bookingDatesEl.classList.remove('is-revealing');

    window.requestAnimationFrame(() => {
      bookingDatesEl.classList.add('is-revealing');
      window.setTimeout(() => {
        bookingDatesEl.classList.remove('is-revealing');
      }, 420);
    });
  }

  function bindTapOnlyActivation(button, activate) {
    // Use the browser's native click handling for touch, pen, mouse and keyboard.
    // Browsers already suppress clicks caused by a real scroll gesture, while
    // still allowing normal finger jitter on mobile to register as a tap.
    button.addEventListener('click', event => {
      if (button.disabled) return;
      activate(event);
    });
  }

  function missingRpcFunction(error) {
    const message = String(
      error?.message || error?.details || error?.hint || error?.code || ''
    );
    return /PGRST202|could not find the function|schema cache/i.test(message);
  }

  function renderDateButton(isoDate, metadata = {}) {
    const [year, month, day] = isoDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'seq-booking-date';
    button.dataset.date = isoDate;
    button.dataset.dateScore = String(Number(metadata.dateScore || 0));
    button.dataset.dayRevenueCents = String(Number(metadata.dayRevenueCents || 0));
    button.innerHTML = `<span>${date.toLocaleDateString('en-AU', { weekday: 'short' })}</span><strong>${date.getDate()}</strong><small>${date.toLocaleDateString('en-AU', { month: 'short' })}</small>`;
    button.setAttribute('aria-label', date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' }));
    button.setAttribute('aria-pressed', 'false');

    const chooseDate = () => {
      const dateAlreadySelected = Boolean(
        selectedDate === isoDate && button.classList.contains('is-active')
      );

      if (dateAlreadySelected) return;

      selectedDate = isoDate;
      resetReservation(false);
      trackFunnel('date_selected', {
        metadata: { selected_date: isoDate }
      });

      els('.seq-booking-date').forEach(item => {
        item.classList.remove('is-active');
        item.setAttribute('aria-pressed', 'false');
      });

      button.classList.add('is-active');
      button.setAttribute('aria-pressed', 'true');
      queueBookingDraftSave();

      const requestedDate = isoDate;
      fetchAvailableTimes().then(() => {
        if (selectedDate !== requestedDate) return;
        bookingTimesEl
          ?.closest('[data-booking-step="time"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });

      updateAppointmentGuidance();
    };

    bindTapOnlyActivation(button, chooseDate, {
      scrollContainers: [bookingDatesEl, bookingScrollEl]
    });

    bookingDatesEl.appendChild(button);
  }

  async function loadNextAvailableDates({ preserveSelection = false } = {}) {
    if (!bookingDatesEl) return;

    const suburb = suburbSelect?.value;
    const requestId = ++availableDatesRequestId;
    const preservedDate = preserveSelection ? selectedDate : null;
    let preserveReservation =
      preserveSelection && hasActiveReservation();

    if (!preserveSelection) {
      selectedDate = null;
      resetReservation(false);
    }

    bookingDatesEl.innerHTML = '';

    if (!suburb) {
      selectedDate = null;
      resetReservation(false);
      bookingDatesEl.innerHTML =
        '<p class="seq-booking-empty seq-booking-empty--dates">Select your suburb and I\u2019ll show my next available appointment dates.</p>';
      if (bookingTimesEl) {
        bookingTimesEl.innerHTML =
          '<p class="seq-booking-empty">Choose a suburb and date to see available times.</p>';
      }
      updateDateScrollerState();
      updateAppointmentGuidance();
      queueBookingDraftSave();
      return;
    }

    renderDateLoadingState();

    /*
      Keep the polished availability transition visible for one short second.
      This prevents a jarring flash when the availability RPC responds instantly.
    */
    const loadingStartedAt = window.performance.now();
    const minimumLoadingTime = 1000;

    const bookingCartToken = await getCartToken();
    let { data, error } = await supabaseClient.rpc('get_smart_available_dates', {
      required_duration: getDurationMinutes(),
      customer_suburb: suburb,
      booking_value_cents: totalPrice(),
      max_results: 4,
      search_days: 60,
      customer_cart_token: bookingCartToken
    });

    // Safe deploy order: if the new Supabase migration has not been applied yet,
    // fall back to the existing availability RPC instead of breaking bookings.
    if (error && missingRpcFunction(error)) {
      ({ data, error } = await supabaseClient.rpc('get_next_available_dates', {
        required_duration: getDurationMinutes(),
        customer_suburb: suburb,
        max_results: 12,
        search_days: 60
      }));
    }

    if (requestId !== availableDatesRequestId) return;

    const elapsedLoadingTime = window.performance.now() - loadingStartedAt;
    const remainingLoadingTime = Math.max(0, minimumLoadingTime - elapsedLoadingTime);

    if (remainingLoadingTime > 0) {
      await new Promise(resolve => window.setTimeout(resolve, remainingLoadingTime));
    }

    if (requestId !== availableDatesRequestId) return;

    if (preserveReservation && !hasActiveReservation()) {
      reservationExpiresAt = null;
      selectedTime = null;
      selectedEndTime = null;
      selectedBookingId = null;
      manageBookingToken = null;
      preserveReservation = false;
    }

    bookingDatesEl.innerHTML = '';
    bookingDatesEl.removeAttribute('aria-busy');

    if (error) {
      console.error('Available date fetch error:', error);
      trackFunnel('booking_failed', {
        metadata: {
          reason: 'availability_dates_rpc_error',
          error_code: String(error?.code || ''),
          backend_message: String(error?.message || error?.details || error?.hint || '').slice(0, 500),
          required_duration_minutes: String(getDurationMinutes())
        }
      });

      if (preserveReservation && preservedDate) {
        renderDateButton(preservedDate);
        bookingDatesEl.querySelector(`[data-date="${preservedDate}"]`)?.classList.add('is-active');
        bookingDatesEl.querySelector(`[data-date="${preservedDate}"]`)?.setAttribute('aria-pressed', 'true');
        selectedDate = preservedDate;
        await fetchAvailableTimes({ preserveReservation: true });
        restoreReservationPresentation();
      } else {
        bookingDatesEl.innerHTML =
          '<p class="seq-booking-empty">Could not load available dates. Please try again.</p>';
      }

      updateDateScrollerState();
      queueBookingDraftSave();
      return;
    }

    const dateRows = [];
    const seenDates = new Set();
    (data || []).forEach(row => {
      const bookingDate = String(row.booking_date || '');
      if (!bookingDate || seenDates.has(bookingDate)) return;
      seenDates.add(bookingDate);
      dateRows.push({
        bookingDate,
        dateScore: Number(row.date_score || 0),
        dayRevenueCents: Number(row.day_revenue_cents || 0),
        projectedRevenueCents: Number(row.projected_revenue_cents || 0)
      });
    });

    const availableDates = dateRows.map(row => row.bookingDate);

    const canRestoreDate = Boolean(
      preservedDate &&
      (availableDates.includes(preservedDate) || preserveReservation)
    );

    if (preserveReservation && preservedDate) {
      renderDateButton(preservedDate);
    }

    dateRows
      .filter(row => !preserveReservation || row.bookingDate !== preservedDate)
      .forEach(row => renderDateButton(row.bookingDate, row));

    if (!availableDates.length && !preserveReservation) {
      selectedDate = null;
      resetReservation(false);
      bookingDatesEl.innerHTML =
        '<div class="seq-date-empty"><strong>No online appointments found</strong><span>I couldn\u2019t find a suitable time in the next 60 days. Please contact me and I\u2019ll see what I can arrange.</span></div>';
      if (bookingTimesEl) {
        bookingTimesEl.innerHTML =
          '<p class="seq-booking-empty">No appointment dates are currently available.</p>';
      }
      updateDateScrollerState();
      updateAppointmentGuidance();
      queueBookingDraftSave();
      return;
    }

    revealAvailableDates();
    configureDateProgressiveDisclosure();

    if (canRestoreDate) {
      selectedDate = preservedDate;
      bookingDatesEl
        .querySelector(`[data-date="${preservedDate}"]`)
        ?.classList.add('is-active');
      bookingDatesEl
        .querySelector(`[data-date="${preservedDate}"]`)
        ?.setAttribute('aria-pressed', 'true');

      await fetchAvailableTimes({
        preserveReservation
      });

      if (preserveReservation) restoreReservationPresentation();
    } else {
      selectedDate = null;
      resetReservation(false);

      if (bookingTimesEl) {
        bookingTimesEl.innerHTML =
          '<p class="seq-booking-empty">Choose a date to see available times.</p>';
      }
    }

    bookingDatesEl.scrollLeft = 0;
    window.requestAnimationFrame(() => {
      updateDateScrollerState();
      nudgeDateScroller();
    });

    updateAppointmentGuidance();
    updateReview();
    queueBookingDraftSave();
  }

  function buildDateButtons() {
    loadNextAvailableDates();
  }

  async function fetchAvailableTimes({ preserveReservation = false } = {}) {
    if (!bookingTimesEl) return;

    const requestId = ++availableTimesRequestId;
    const requestedDate = selectedDate;
    const requestedSuburb = suburbSelect?.value || '';
    const shouldPreserveReservation =
      preserveReservation && hasActiveReservation();

    if (!requestedDate || !requestedSuburb) {
      bookingTimesEl.innerHTML =
        '<p class="seq-booking-empty">Choose a suburb and date to see available times.</p>';
      return;
    }

    bookingTimesEl.innerHTML =
      '<p class="seq-booking-empty">Loading available times\u2026</p>';

    const bookingCartToken = await getCartToken();
    let { data, error } = await supabaseClient.rpc('get_smart_bookable_slots', {
      target_date: requestedDate,
      required_duration: getDurationMinutes(),
      customer_suburb: requestedSuburb,
      booking_value_cents: totalPrice(),
      customer_cart_token: bookingCartToken
    });

    if (error && missingRpcFunction(error)) {
      ({ data, error } = await supabaseClient.rpc('get_bookable_slots', {
        target_date: requestedDate,
        required_duration: getDurationMinutes(),
        customer_suburb: requestedSuburb
      }));
    }

    if (
      requestId !== availableTimesRequestId
      || selectedDate !== requestedDate
      || suburbSelect?.value !== requestedSuburb
    ) return;

    const heldTime = shouldPreserveReservation
      ? normalizeStoredTime(selectedTime)
      : null;

    function createTimeButton(time, { held = false, helperText = 'Available' } = {}) {
      const normalizedTime = normalizeStoredTime(time);
      if (!normalizedTime) return null;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'seq-booking-time';
      button.dataset.time = normalizedTime;
      button.innerHTML = held
        ? `<strong>${formatTime(normalizedTime)}</strong><small>Held for you</small>`
        : `<strong>${formatTime(normalizedTime)}</strong><small>${helperText}</small>`;
      button.setAttribute('aria-pressed', held ? 'true' : 'false');
      button.setAttribute(
        'aria-label',
        held
          ? `${formatTime(normalizedTime)} appointment held for you`
          : `${formatTime(normalizedTime)} appointment`
      );
      bindTapOnlyActivation(button, () => {
        trackFunnel('slot_selected', {
          metadata: { selected_time: normalizedTime, selected_date: selectedDate || '' }
        });
        reserveSelectedSlot(normalizedTime, button);
      }, {
        scrollContainers: [bookingTimesEl, bookingScrollEl]
      });

      if (held) button.classList.add('is-active');
      return button;
    }

    bookingTimesEl.innerHTML = '';

    if (heldTime) {
      const heldButton = createTimeButton(heldTime, { held: true });
      if (heldButton) bookingTimesEl.appendChild(heldButton);
    }

    if (error) {
      console.error('Slot fetch error:', error);
      trackFunnel('booking_failed', {
        metadata: {
          reason: 'availability_slots_rpc_error',
          error_code: String(error?.code || ''),
          backend_message: String(error?.message || error?.details || error?.hint || '').slice(0, 500),
          required_duration_minutes: String(getDurationMinutes()),
          requested_date: requestedDate
        }
      });

      if (!heldTime) {
        bookingTimesEl.innerHTML =
          '<p class="seq-booking-empty">Could not load times. Please try another date.</p>';
      } else {
        const note = document.createElement('p');
        note.className = 'seq-booking-empty';
        note.textContent =
          'Your held appointment is still saved. Other available times could not be loaded right now.';
        bookingTimesEl.appendChild(note);
        restoreReservationPresentation();
      }

      return;
    }

    const availableSlotRows = (data || [])
      .map(slot => ({
        time: normalizeStoredTime(slot.slot_time),
        score: Number(slot.slot_score || 0),
        fitLabel: String(slot.fit_label || 'Available')
      }))
      .filter(slot => slot.time && slot.time !== heldTime);

    // Supabase now returns only the deliberately curated business-fit slots.
    // Do not expose weaker mathematically-valid alternatives in the UI.
    const curatedSlots = [...availableSlotRows].sort((a, b) =>
      a.time.localeCompare(b.time)
    );

    if (curatedSlots.length) {
      const group = document.createElement('section');
      group.className = 'seq-time-group';
      group.innerHTML = `<header><strong>Available times</strong><small>${curatedSlots.length} available</small></header><div class="seq-time-group__options"></div>`;
      const options = group.querySelector('.seq-time-group__options');

      curatedSlots.forEach(slot => {
        const button = createTimeButton(slot.time, { helperText: 'Available' });
        if (!button) return;
        options.appendChild(button);
      });

      bookingTimesEl.appendChild(group);
    }

    if (!bookingTimesEl.querySelector('.seq-booking-time')) {
      bookingTimesEl.innerHTML =
        '<p class="seq-booking-empty">No times remain on this date. Please choose another date.</p>';
      return;
    }

    if (heldTime) restoreReservationPresentation();
    queueBookingDraftSave();
  }

  function getCartToken() {
    if (cartTokenPromise) return cartTokenPromise;

    cartTokenPromise = Promise.resolve().then(() => {
      const storageKey = 'seq_booking_session_token';
      let token = null;

      try {
        token =
          localStorage.getItem(storageKey) ||
          sessionStorage.getItem(storageKey);
      } catch (error) {
        // Storage can be unavailable in privacy-restricted browsers.
      }

      if (!token) {
        const randomPart = window.crypto?.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

        token = `seq_${randomPart}`;
      }

      try {
        localStorage.setItem(storageKey, token);
        sessionStorage.setItem(storageKey, token);
      } catch (error) {
        // The in-memory token still keeps this page session working.
      }

      return token;
    });

    return cartTokenPromise;
  }

  function clearReservationError() {
    el('[data-reservation-error]')?.remove();
  }

  function showReservationError(message) {
    clearReservationError();

    const error = document.createElement('p');
    error.className = 'seq-reservation-error';
    error.dataset.reservationError = '';
    error.setAttribute('role', 'alert');
    error.textContent = message;

    bookingTimesEl?.insertAdjacentElement('afterend', error);
  }

  async function reserveSelectedSlot(time, button) {
    if (!selectedDate || !suburbSelect?.value || reservationInFlight) return;

    // Tapping the already-held time should not create another reservation request.
    if (selectedBookingId && selectedTime === time) return;

    clearReservationError();
    reservationInFlight = true;

    const requestId = ++reservationRequestId;
    const previousExpiry = reservationExpiresAt;
    const isSwitching = Boolean(selectedBookingId && selectedTime !== time);
    const originalContent = button.innerHTML;

    els('.seq-booking-time').forEach(item => {
      item.disabled = true;
      item.classList.toggle('is-switch-target', item === button);
    });

    button.innerHTML = isSwitching
      ? '<strong>Switching\u2026</strong><small>Moving your hold</small>'
      : '<strong>Holding\u2026</strong><small>Reserving this time</small>';

    try {
      let data = null;
      let error = null;

      if (internalTestMode) {
        // Test mode must never reserve a real appointment slot or create a
        // booking record. Simulate the hold locally while still recording
        // the funnel event as test data.
        await new Promise(resolve => window.setTimeout(resolve, 250));
        const makeUuid = () => window.crypto?.randomUUID
          ? window.crypto.randomUUID()
          : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
              const random = Math.random() * 16 | 0;
              const value = character === 'x' ? random : (random & 0x3 | 0x8);
              return value.toString(16);
            });

        data = [{
          booking_id: makeUuid(),
          reschedule_token: makeUuid(),
          reserved_until: new Date(Date.now() + 10 * 60 * 1000).toISOString()
        }];
      } else {
        const bookingCartToken = await getCartToken();
        let reservationResult = await supabaseClient.rpc('reserve_smart_booking_slot', {
          target_date: selectedDate,
          target_start: time,
          required_duration: getDurationMinutes(),
          indoor_units: units,
          outdoor_units: outdoorUnits,
          customer_suburb: suburbSelect.value,
          customer_cart_token: bookingCartToken,
          booking_value_cents: totalPrice()
        });

        if (reservationResult.error && missingRpcFunction(reservationResult.error)) {
          reservationResult = await supabaseClient.rpc('reserve_booking_slot', {
            target_date: selectedDate,
            target_start: time,
            required_duration: getDurationMinutes(),
            indoor_units: units,
            outdoor_units: outdoorUnits,
            customer_suburb: suburbSelect.value,
            customer_cart_token: bookingCartToken
          });
        }

        data = reservationResult.data;
        error = reservationResult.error;
      }

      if (requestId !== reservationRequestId) return;

      if (error || !data?.[0]?.booking_id || !data?.[0]?.reschedule_token) {
        const backendMessage =
          error?.message ||
          error?.details ||
          error?.hint ||
          'Reservation request returned no booking.';

        console.error('Reservation error:', {
          backendMessage,
          error,
          data
        });
        trackFunnel('booking_failed', {
          metadata: {
            reason: 'reservation_rpc_error',
            error_code: String(error?.code || ''),
            backend_message: String(backendMessage || '').slice(0, 500),
            required_duration_minutes: String(getDurationMinutes()),
            requested_date: selectedDate || '',
            requested_time: time
          }
        });

        button.innerHTML = originalContent;

        const genuinelyUnavailable =
          /no longer available|slot|overlap|unavailable/i.test(backendMessage);

        showReservationError(
          isSwitching
            ? genuinelyUnavailable
              ? 'That time was just taken. Your previous appointment is still held.'
              : 'I couldn\u2019t move your hold. Your previous appointment is still held.'
            : genuinelyUnavailable
              ? 'That time was just taken. Please choose another available time.'
              : 'I couldn\u2019t hold that time. Please try again.'
        );

        if (previousExpiry) startReservationCountdown(previousExpiry);
        await fetchAvailableTimes();
        return;
      }

      selectedTime = time;
      selectedEndTime = calculateEndTime(time, getDurationMinutes());
      markBookingStarted('time_selected');
      pushOfferEvent('seq_booking_time_selected', {
        booking_date: selectedDate,
        booking_time: time
      });
      selectedBookingId = data[0].booking_id;
      manageBookingToken = data[0].reschedule_token;
      reservationExpiresAt = data[0].reserved_until || null;
      trackFunnel('slot_reserved', {
        bookingId: selectedBookingId,
        metadata: { selected_time: time, selected_date: selectedDate || '' }
      });

      els('.seq-booking-time').forEach(item => {
        item.classList.remove('is-active', 'is-switch-target');
        item.setAttribute('aria-pressed', 'false');
      });

      button.classList.add('is-active');
      button.setAttribute('aria-pressed', 'true');
      button.innerHTML = originalContent;

      if (bookingSelectedTextEl) {
        bookingSelectedTextEl.textContent =
          `${formatDateFromISO(selectedDate)} at ${formatTime(selectedTime)}`;
      }

      if (bookingSelectedEl) {
        bookingSelectedEl.hidden = false;
        bookingSelectedEl.classList.remove('is-expired');
      }

      startReservationCountdown(reservationExpiresAt);

      if (progressAppointmentEl) {
        progressAppointmentEl.textContent =
          `${formatDateFromISO(selectedDate)} \u00B7 ${formatTime(selectedTime)}`;
      }

      updateAppointmentGuidance();
      updateReview();
      queueBookingDraftSave();
    } finally {
      reservationInFlight = false;

      els('.seq-booking-time').forEach(item => {
        item.disabled = false;
        item.classList.remove('is-switch-target');
      });
    }
  }

  function normalizeAustralianMobile(value) {
    let digits = String(value || '').replace(/\D/g, '');
    if (digits.startsWith('61')) digits = `0${digits.slice(2)}`;
    return /^04\d{8}$/.test(digits) ? digits : null;
  }

  function formatAustralianMobileDisplay(value) {
    let digits = String(value || '').replace(/\D/g, '');
    if (digits.startsWith('61')) digits = `0${digits.slice(2)}`;
    digits = digits.slice(0, 10);

    if (!digits) return '';
    if (!digits.startsWith('04')) return String(value || '');

    const first = digits.slice(0, 4);
    const second = digits.slice(4, 7);
    const third = digits.slice(7, 10);

    return [first, second, third].filter(Boolean).join(' ');
  }

  function formatMobileInputForDisplay() {
    if (!mobileInput) return;

    const formatted = formatAustralianMobileDisplay(mobileInput.value);
    if (formatted && mobileInput.value !== formatted) {
      mobileInput.value = formatted;
    }
  }

  function updateReview() {
    const reviewSuburb = el('[data-review-suburb]');
    const reviewAppointment = el('[data-review-appointment]');
    if (reviewSuburb) reviewSuburb.textContent = suburbSelect?.value || '\u2014';
    if (reviewAppointment) reviewAppointment.textContent = selectedDate && selectedTime
      ? `${formatDateFromISO(selectedDate)} at ${formatTime(selectedTime)}`
      : '\u2014';
    updatePricing();
    updateDirectBookingGate(false);
  }

  function cleanCustomerName(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').slice(0, 100);
  }

  function cleanServiceAddress(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').slice(0, 180);
  }

  function escapeDirectBookingHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function showDirectBookingError(message = '') {
    if (!directBookingError) return;
    const text = String(message || '').trim();
    directBookingError.hidden = !text;
    directBookingError.textContent = text;
  }

  function directBookingValidity() {
    const name = cleanCustomerName(customerNameInput?.value);
    const mobile = normalizeAustralianMobile(mobileInput?.value);
    const address = cleanServiceAddress(serviceAddressInput?.value);

    return {
      name,
      mobile,
      address,
      nameValid: name.length >= 2,
      mobileValid: Boolean(mobile),
      addressValid: address.length >= 5
    };
  }

  function updateDirectBookingGate(showErrors) {
    if (!addService) return false;
    if (showErrors) {
      detailsAttempted = true;
      mobileAttempted = true;
    }

    const validity = directBookingValidity();
    const revealErrors = detailsAttempted || mobileAttempted;

    if (customerNameError) {
      customerNameError.hidden = !(revealErrors && !validity.nameValid);
    }
    if (mobileError) {
      mobileError.hidden = !(revealErrors && !validity.mobileValid);
    }
    if (serviceAddressError) {
      serviceAddressError.hidden = !(revealErrors && !validity.addressValid);
    }

    customerNameInput?.classList.toggle(
      'is-invalid',
      revealErrors && !validity.nameValid
    );
    mobileInput?.classList.toggle(
      'is-invalid',
      revealErrors && !validity.mobileValid
    );
    serviceAddressInput?.classList.toggle(
      'is-invalid',
      revealErrors && !validity.addressValid
    );

    const ready = Boolean(
      selectedBookingId &&
      manageBookingToken &&
      selectedDate &&
      selectedTime &&
      validity.nameValid &&
      validity.mobileValid &&
      validity.addressValid &&
      !bookingSubmissionInFlight
    );

    addService.disabled = !ready;
    addService.classList.toggle('is-disabled', !ready);

    if (bookingSubmissionInFlight) {
      addService.textContent = 'Confirming Appointment\u2026';
    } else if (!selectedBookingId) {
      addService.textContent = 'Choose Appointment First';
    } else if (!validity.nameValid) {
      addService.textContent = 'Enter Your Name';
    } else if (!validity.mobileValid) {
      addService.textContent = 'Enter Mobile Number';
    } else if (!validity.addressValid) {
      addService.textContent = 'Enter Service Address';
    } else {
      addService.textContent = 'Confirm My Appointment';
    }

    return ready;
  }

  // Retained for compatibility with existing calls in this booking engine.
  function updateMobileGate(showError) {
    return updateDirectBookingGate(showError);
  }

  function focusFirstInvalidDetail() {
    const validity = directBookingValidity();
    if (!validity.nameValid) customerNameInput?.focus();
    else if (!validity.mobileValid) mobileInput?.focus();
    else if (!validity.addressValid) serviceAddressInput?.focus();
  }

  function directBookingEndpoint() {
    return String(
      section.dataset.directBookingEndpoint ||
      'https://google-reviews-sync-production.up.railway.app/api/public/bookings/confirm'
    ).trim();
  }

  function unitSummaryForSuccess(indoorCount, outdoorCount) {
    const parts = [
      `${indoorCount} indoor unit${indoorCount === 1 ? '' : 's'}`
    ];
    if (outdoorCount > 0) {
      parts.push(
        `${outdoorCount} outdoor unit${outdoorCount === 1 ? '' : 's'}`
      );
    }
    return parts.join(' \u00B7 ');
  }

  function renderDirectBookingSuccess(payload) {
    const booking = payload?.booking || {};
    const testMode = Boolean(payload?.testMode);
    const successPanel = stepThreePanel || el('[data-step-panel="3"]');
    if (!successPanel) return;

    const confirmedIndoorUnits = Number(booking.indoorUnits || units || 1);
    const confirmedOutdoorUnits = Number(booking.outdoorUnits ?? outdoorUnits ?? 0);
    const fullAddress = [booking.address, booking.suburb]
      .filter(Boolean)
      .join(', ');
    const total = formatMoney(Number(booking.priceCents ?? totalPrice()));
    const smsPending = Boolean(payload?.smsPending);

    section.classList.add('is-direct-booking-confirmed');
    if (!testMode) saveReturningCustomerProfile(booking);
    stopReservationCountdown();

    successPanel.innerHTML = `
      <section class="seq-booking-success" aria-labelledby="seq-booking-success-title" role="status">
        <span class="seq-booking-success__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M6.5 12.5l3.4 3.4L17.8 8" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </span>
        <span class="seq-booking-success__eyebrow">${testMode ? 'Internal test completed' : 'Appointment confirmed'}</span>
        <h3 id="seq-booking-success-title">${testMode
          ? 'Test complete. Nothing was booked.'
          : `You\u2019re booked${cleanCustomerName(customerNameInput?.value).split(' ')[0] ? `, ${escapeDirectBookingHtml(cleanCustomerName(customerNameInput?.value).split(' ')[0])}` : ''}!`}</h3>
        <p>${testMode
          ? 'This was a simulated booking. No appointment, customer record, Shopify order, calendar event, SMS or advertising conversion was created.'
          : smsPending
            ? 'Your appointment is secured. Your SMS confirmation is being sent now and may take another moment to arrive.'
            : 'Your confirmation and manage-booking link have been sent by SMS.'}</p>

        <div class="seq-booking-success__summary">
          <div>
            <span>Appointment</span>
            <strong>${escapeDirectBookingHtml(booking.bookingDateDisplay || formatDateFromISO(selectedDate))}</strong>
            <small>${escapeDirectBookingHtml(booking.startTimeDisplay || formatTime(selectedTime))}</small>
          </div>
          <div>
            <span>Service address</span>
            <strong>${escapeDirectBookingHtml(fullAddress)}</strong>
          </div>
          <div>
            <span>Service</span>
            <strong>${escapeDirectBookingHtml(unitSummaryForSuccess(confirmedIndoorUnits, confirmedOutdoorUnits))}</strong>
          </div>
          <div class="seq-booking-success__total">
            <span>Pay after service</span>
            <strong>${escapeDirectBookingHtml(total)}</strong>
          </div>
        </div>

        <div class="seq-booking-success__actions">
          ${testMode ? '' : `
            <a class="seq-booking-success__primary" href="${escapeDirectBookingHtml(booking.manageUrl || `/pages/manage-booking?token=${manageBookingToken}`)}">
              Manage My Booking
            </a>
          `}
          <button type="button" class="seq-booking-success__secondary" data-direct-booking-close>
            Return to Website
          </button>
        </div>

        <small class="seq-booking-success__note">${testMode
          ? 'Test activity is excluded from live analytics by default.'
          : 'Manage or reschedule online from your customer portal. No upfront payment was taken.'}</small>
      </section>
    `;

    successPanel
      .querySelector('[data-direct-booking-close]')
      ?.addEventListener('click', () => {
        containingBookingDialog?.close('booking_confirmed');
      });

    trackFunnel('booking_confirmed', {
      bookingId: booking.id || selectedBookingId,
      suburb: booking.suburb || suburbSelect?.value || '',
      indoorUnits: confirmedIndoorUnits,
      outdoorUnits: confirmedOutdoorUnits,
      valueCents: Number(booking.priceCents ?? totalPrice()),
      metadata: { selected_date: selectedDate || '', selected_time: selectedTime || '' }
    });

    const confirmedBookingId = String(
      booking.id || selectedBookingId || ''
    );
    const confirmedBookingValue =
      Number(booking.priceCents ?? totalPrice()) / 100;

    if (!internalTestMode) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'seq_booking_confirmed',
        booking_id: confirmedBookingId,
        booking_value: confirmedBookingValue,
        currency: 'AUD',
        indoor_units: confirmedIndoorUnits,
        outdoor_units: confirmedOutdoorUnits,
        suburb: booking.suburb || suburbSelect?.value || ''
      });

      // Record the Google Ads conversion only after the booking server has
      // confirmed the appointment successfully.
      if (
        confirmedBookingId &&
        Number.isFinite(confirmedBookingValue) &&
        confirmedBookingValue > 0 &&
        typeof window.gtag === 'function'
      ) {
        window.gtag('event', 'conversion', {
          send_to: 'AW-18336663661/vyDsCNeFktscEO2QzadE',
          value: confirmedBookingValue,
          currency: 'AUD',
          transaction_id: confirmedBookingId
        });
      }

      // Record the Meta Schedule event only after the booking server has
      // confirmed the appointment successfully.
      if (
        confirmedBookingId &&
        Number.isFinite(confirmedBookingValue) &&
        confirmedBookingValue > 0 &&
        typeof window.fbq === 'function'
      ) {
        window.fbq(
          'track',
          'Schedule',
          {
            value: confirmedBookingValue,
            currency: 'AUD'
          },
          {
            eventID: confirmedBookingId
          }
        );
      }

      if (typeof window.clarity === 'function') {
        window.clarity('event', 'seq_booking_confirmed');
      }
    } else {
      console.info('SEQ test mode: Google Ads, Meta and Clarity booking conversions were suppressed.');
    }

    scrollBookingToTop('smooth');
  }

  async function completeBooking() {
    showDirectBookingError('');
    trackFunnel('confirmation_attempted');

    if (!updateDirectBookingGate(true)) {
      trackFunnel('booking_failed', { metadata: { reason: 'invalid_customer_details' } });
      focusFirstInvalidDetail();
      return;
    }

    if (!selectedBookingId || !manageBookingToken || !selectedDate || !selectedTime) {
      showDirectBookingError(
        'Your appointment hold is incomplete. Please choose the appointment again.'
      );
      trackFunnel('booking_failed', { metadata: { reason: 'appointment_hold_incomplete' } });
      return;
    }

    const validity = directBookingValidity();
    bookingSubmissionInFlight = true;
    stopReservationCountdown();
    updateDirectBookingGate(false);
    addService?.setAttribute('aria-busy', 'true');

    try {
      if (internalTestMode) {
        // Complete the whole customer journey without calling the live
        // confirmation endpoint or creating any operational records.
        const payload = {
          success: true,
          testMode: true,
          smsPending: false,
          booking: {
            id: selectedBookingId,
            customerName: validity.name,
            mobile: validity.mobile,
            address: validity.address,
            suburb: suburbSelect?.value || '',
            bookingDate: selectedDate,
            bookingDateDisplay: formatDateFromISO(selectedDate),
            startTime: selectedTime,
            startTimeDisplay: formatTime(selectedTime),
            indoorUnits: units,
            outdoorUnits,
            priceCents: totalPrice(),
            discountCode: winterSpecialApplied ? winterDiscountCode : null,
            manageUrl: ''
          }
        };

        bookingDraftCompleted = true;
        clearBookingDraft();
        renderDirectBookingSuccess(payload);
        return;
      }

      const response = await fetch(directBookingEndpoint(), {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        // Keep this as a CORS-safelisted request so the browser sends the
        // booking POST directly. React Router's basic app server rejects
        // cross-origin OPTIONS preflight requests before route loaders/actions.
        headers: {
          Accept: 'application/json'
        },
        body: JSON.stringify({
          bookingId: selectedBookingId,
          manageBookingToken,
          customerName: validity.name,
          customerMobile: validity.mobile,
          serviceAddressLine1: validity.address,
          discountCode: winterSpecialApplied ? winterDiscountCode : null
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.success) {
        const error = new Error(
          payload?.error || 'The appointment could not be confirmed. Please try again.'
        );
        error.status = response.status;
        throw error;
      }

      bookingDraftCompleted = true;
      clearBookingDraft();
      renderDirectBookingSuccess(payload);
    } catch (error) {
      console.error('Direct booking confirmation failed:', error);
      trackFunnel('booking_failed', {
        metadata: {
          reason: Number(error?.status) === 409 ? 'appointment_hold_expired' : 'confirmation_request_failed'
        }
      });

      if (Number(error?.status) === 409) {
        bookingSubmissionInFlight = false;
        addService?.removeAttribute('aria-busy');
        resetReservation(false);
        selectedDate = null;
        showStep(2);
        await loadNextAvailableDates();
        window.alert(
          error.message ||
          'That appointment hold has expired. Please choose another available time.'
        );
        return;
      }

      showDirectBookingError(
        error.message ||
        'Something went wrong confirming your appointment. Your held time has not been released yet, so please try again.'
      );
      startReservationCountdown(reservationExpiresAt);
    } finally {
      bookingSubmissionInFlight = false;
      addService?.removeAttribute('aria-busy');
      if (!bookingDraftCompleted) updateDirectBookingGate(false);
    }
  }

  function parseCartISODate(value) {
    const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})T/);
    return match ? match[1] : null;
  }

  function parseCartISOTime(value) {
    const match = String(value || '').match(/T(\d{2}:\d{2})(?::\d{2})?/);
    return match ? `${match[1]}:00` : null;
  }

  async function loadCartBooking(booking) {
    if (!booking) return;

    isRestoringBookingDraft = true;
    bookingDraftCompleted = false;

    units = Math.max(1, Math.min(10, Number(booking.indoorUnits) || 1));
    outdoorUnits = Math.max(0, Math.min(units, Number(booking.outdoorUnits) || 0));

    const matchingSuburb = Array.from(suburbSelect?.options || []).find(
      option => option.value.toLowerCase() === String(booking.suburb || '').trim().toLowerCase()
    );
    if (suburbSelect && matchingSuburb) suburbSelect.value = matchingSuburb.value;
    if (mobileInput) {
      mobileInput.value = formatAustralianMobileDisplay(
        String(booking.mobile || '')
      );
    }

    selectedDate = parseCartISODate(booking.bookingStartISO) || parseStoredDate(booking.selectedDate);
    selectedTime = parseCartISOTime(booking.bookingStartISO) || normalizeStoredTime(booking.selectedTime);
    selectedEndTime = parseCartISOTime(booking.bookingEndISO) || normalizeStoredTime(booking.selectedEndTime);
    selectedBookingId = booking.bookingId || null;
    manageBookingToken = booking.manageBookingToken || null;
    reservationExpiresAt = booking.reservationExpiresAt ? new Date(booking.reservationExpiresAt) : null;
    winterSpecialApplied = Boolean(booking.winterSpecial);

    if (booking.chooseNewTime || booking.expired) {
      stopReservationCountdown();
      reservationExpiresAt = null;
      selectedTime = null;
      selectedEndTime = null;
      selectedBookingId = null;
      manageBookingToken = null;
    }

    currentStep = 2;
    isRestoringBookingDraft = false;

    updatePricing();
    updateAppointmentGuidance();
    updateReview();

    await loadNextAvailableDates({ preserveSelection: !booking.expired });

    if (selectedBookingId && selectedTime && reservationExpiresAt instanceof Date && reservationExpiresAt.getTime() > Date.now()) {
      if (bookingSelectedTextEl) {
        bookingSelectedTextEl.textContent = `${formatDateFromISO(selectedDate)} at ${formatTime(selectedTime)}`;
      }
      if (bookingSelectedEl) bookingSelectedEl.hidden = false;
      startReservationCountdown(reservationExpiresAt);
    }

    showStep(2);
    saveBookingDraft();
  }

  async function releaseBookingHold(booking) {
    if (!booking?.bookingId || !booking?.manageBookingToken) return;
    try {
      const { error } = await supabaseClient.rpc('release_booking_hold', {
        target_booking_id: booking.bookingId,
        target_reschedule_token: booking.manageBookingToken
      });
      if (error) console.warn('The cart was removed, but the temporary hold could not be released immediately.', error);
    } catch (error) {
      console.warn('The cart was removed, but the temporary hold could not be released immediately.', error);
    }
  }

  async function removeCartBooking(booking) {
    await releaseBookingHold(booking);

    for (const key of booking?.itemKeys || []) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      if (!response.ok) throw new Error('Could not remove a booking item from the cart');
    }

    const clearedAttributes = {};
    [
      'Booking Date', 'Booking Time', 'Booking End Time', 'Booking Start ISO',
      'Booking End ISO', 'Booking Timezone', 'Booking ID', 'Manage Booking Token',
      'Reservation Expires At', 'Cleaning Duration Minutes', 'Suburb', 'Indoor Units',
      'Outdoor Units', 'Customer Mobile', 'Pricing', 'Winter Special', 'Next Step'
    ].forEach(key => { clearedAttributes[key] = ''; });

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: clearedAttributes })
    });

    clearBookingDraft();
    bookingDraftCompleted = false;
    units = 1;
    outdoorUnits = 0;
    currentStep = 1;
    if (suburbSelect) suburbSelect.value = '';
    if (mobileInput) mobileInput.value = '';
    selectedDate = null;
    resetReservation(false);
    updatePricing();
    updateAppointmentGuidance();
    updateReview();
    await loadNextAvailableDates();
    showStep(1);
  }

  section.addEventListener('seq:load-cart-booking', event => {
    void loadCartBooking(event.detail);
  });

  section.addEventListener('seq:cart-booking-removed', () => {
    clearBookingDraft();
    bookingDraftCompleted = false;
  });

  window.SEQBookingEngine = {
    ...(window.SEQBookingEngine || {}),
    loadCartBooking,
    removeCartBooking
  };

  el('[data-action="minus"]')?.addEventListener('click', () => {
    const previousUnits = units;
    units = Math.max(1, units - 1);
    if (outdoorUnits > units) outdoorUnits = units;
    if (units !== previousUnits) {
      markBookingStarted('unit_quantity');
      trackFunnel('service_quantity_changed', { metadata: { reason: 'indoor_decreased' } });
    }
    updatePricing();
    resetReservation(false);
    loadNextAvailableDates();
  });
  el('[data-action="plus"]')?.addEventListener('click', () => {
    const previousUnits = units;
    units = Math.min(10, units + 1);
    if (units !== previousUnits) {
      markBookingStarted('unit_quantity');
      trackFunnel('service_quantity_changed', { metadata: { reason: 'indoor_increased' } });
    }
    updatePricing();
    resetReservation(false);
    loadNextAvailableDates();
  });
  el('[data-outdoor-action="minus"]')?.addEventListener('click', () => {
    const previousOutdoorUnits = outdoorUnits;
    outdoorUnits = Math.max(0, outdoorUnits - 1);
    if (outdoorUnits !== previousOutdoorUnits) {
      markBookingStarted('unit_quantity');
      trackFunnel('service_quantity_changed', { metadata: { reason: 'outdoor_decreased' } });
    }
    updatePricing();
    resetReservation(false);
    loadNextAvailableDates();
  });
  el('[data-outdoor-action="plus"]')?.addEventListener('click', () => {
    const previousOutdoorUnits = outdoorUnits;
    outdoorUnits = Math.min(units, outdoorUnits + 1);
    if (outdoorUnits !== previousOutdoorUnits) {
      markBookingStarted('unit_quantity');
      trackFunnel('service_quantity_changed', { metadata: { reason: 'outdoor_increased' } });
    }
    updatePricing();
    resetReservation(false);
    loadNextAvailableDates();
  });

  suburbSelect?.addEventListener('change', () => {
    if (suburbSelect.value) {
      markBookingStarted('suburb_selected');
      trackFunnel('suburb_selected', { suburb: suburbSelect.value });
    }
    selectedDate = null;
    resetReservation(false);
    updateAppointmentGuidance();
    loadNextAvailableDates();
  });

  els('[data-step-next]').forEach(button => button.addEventListener('click', () => {
    const next = Number(button.dataset.stepNext);
    if (next === 2) {
      appointmentSelectorOpened = true;
      markBookingStarted('appointment_selector_opened');
    }
    if (next === 3 && !selectedBookingId) return;
    showStep(next);
  }));
  els('[data-step-prev]').forEach(button => button.addEventListener('click', () => showStep(Number(button.dataset.stepPrev))));
  els('[data-step-jump]').forEach(button => button.addEventListener('click', () => {
    const step = Number(button.dataset.stepJump);
    if (step === 2) {
      appointmentSelectorOpened = true;
      markBookingStarted('appointment_selector_opened');
    }
    if (step === 3 && !selectedBookingId) return;
    showStep(step);
  }));

  mobileInput?.addEventListener('input', () => {
    if (normalizeAustralianMobile(mobileInput.value)) {
      mobileAttempted = false;
      formatMobileInputForDisplay();
    }
    updateMobileGate(false);
    queueBookingDraftSave();
  });
  mobileInput?.addEventListener('blur', () => {
    formatMobileInputForDisplay();
    updateMobileGate(Boolean(mobileInput.value.trim()));
    saveBookingDraft();
  });

  [customerNameInput, serviceAddressInput].forEach(input => {
    input?.addEventListener('input', () => {
      showDirectBookingError('');
      updateDirectBookingGate(false);
      queueBookingDraftSave();
    });
    input?.addEventListener('blur', () => {
      updateDirectBookingGate(Boolean(input.value.trim()));
      saveBookingDraft();
    });
  });

  addService?.addEventListener('click', completeBooking);

  bookingDatesEl?.addEventListener('pointerdown', () => {
    appointmentSelectorOpened = true;
    markBookingStarted('appointment_selector_opened');
  }, { passive: true });

  bookingDatesEl?.addEventListener('focusin', () => {
    appointmentSelectorOpened = true;
    markBookingStarted('appointment_selector_opened');
  });

  bookingDatesEl?.addEventListener('scroll', () => {
    appointmentSelectorOpened = true;
    markBookingStarted('appointment_selector_opened');
    updateDateScrollerState();
    bookingDatesEl.closest('[data-booking-step="date"]')?.classList.add('has-date-scroll-interaction');
  }, { passive: true });

  offerBackdrop?.addEventListener('click', () => {
    saveOfferResponse('backdrop_dismissed');
    closeOfferSheet({ closeAutoOpenedBooking: true });
  });
  containingBookingDialog?.addEventListener('seq:offer-close-requested', () => {
    saveOfferResponse('escape_dismissed');
    closeOfferSheet({ closeAutoOpenedBooking: true });
  });
  containingBookingDialog?.addEventListener('seq:offer-show-requested', event => {
    const shown = openOfferSheet({
      force: Boolean(event.detail?.force),
      trigger: event.detail?.trigger || 'manual'
    });
    containingBookingDialog.dispatchEvent(new CustomEvent('seq:offer-show-result', {
      bubbles: false,
      detail: { shown }
    }));
  });
  offerEl('[data-offer-close]')?.addEventListener('click', () => {
    pulseHaptic(8);
    saveOfferResponse('close_dismissed');
    closeOfferSheet({ closeAutoOpenedBooking: true });
  });
  offerEl('[data-offer-decline]')?.addEventListener('click', () => {
    pulseHaptic(8);
    saveOfferResponse('discount_declined');
    pushOfferEvent('seq_winter_offer_declined');
    closeOfferSheet({ closeAutoOpenedBooking: true });
  });
  offerEls('[data-offer-continue]').forEach(button => button.addEventListener('click', () => {
    pulseHaptic(8);
    closeOfferSheet();
    scrollBookingToTop();
  }));
  offerEl('[data-offer-apply]')?.addEventListener('click', applyWinterSpecial);

  offerEl('[data-offer-call]')?.addEventListener('click', () => {
    pulseHaptic(12);
    pushOfferEvent('seq_booking_question_contact', { method: 'call', phone: '0407090330' });
  });
  offerEl('[data-offer-sms]')?.addEventListener('click', () => {
    pulseHaptic(12);
    pushOfferEvent('seq_booking_question_contact', { method: 'sms', phone: '0407090330' });
  });

  offerEls('[data-offer-reason]').forEach(button => button.addEventListener('click', () => {
    pulseHaptic(10);
    const reason = button.dataset.offerReason;
    saveOfferResponse(reason);
    pushOfferEvent('seq_booking_feedback_selected', { reason });

    if (reason === 'too_expensive') {
      setOfferView('discount');
      pushOfferEvent('seq_winter_offer_shown', {
        discount_code: winterDiscountCode,
        discount_value: outdoorUnits * outdoorPrice / 100
      });
    } else if (reason === 'just_comparing') {
      setOfferView('comparing');
    } else if (reason === 'have_a_question') {
      setOfferView('question');
    } else {
      setOfferView('thanks');
    }
  }));

  section.addEventListener('seq:booking-context', event => {
    applyPersonalisationContext(event.detail || {});
  });

  if (containingBookingDialog) {
    const recordModalOpen = trigger => {
      if (!containingBookingDialog.open) return;
      trackFunnelOnce('booking_modal_opened', 'booking_modal_opened', {
        metadata: { trigger: trigger || 'dialog_open' }
      });
    };

    if (containingBookingDialog.open) recordModalOpen('initial_open');
    const dialogObserver = new MutationObserver(() => {
      if (containingBookingDialog.open) recordModalOpen('open_attribute');
    });
    dialogObserver.observe(containingBookingDialog, { attributes: true, attributeFilter: ['open'] });

    containingBookingDialog.addEventListener('seq:modal-closing', event => {
      saveBookingDraft();
      if (offerIsOpen) closeOfferSheet();
      if (!section.classList.contains('is-direct-booking-confirmed')) {
        trackFunnel('booking_modal_closed', {
          step: currentStep,
          metadata: {
            reason: event.detail?.reason || containingBookingDialog.returnValue || 'customer_closed',
            requested_step: String(currentStep)
          }
        });
      }
    });
    containingBookingDialog.addEventListener('seq:modal-closed', () => {
      if (offerIsOpen) closeOfferSheet();
      if (
        bookingDraftCompleted &&
        section.classList.contains('is-direct-booking-confirmed')
      ) {
        window.setTimeout(() => window.location.reload(), 40);
      }
    });
    containingBookingDialog.addEventListener('seq:offer-reset', () => {
      removeSessionValue(offerSessionSeenKey);
      removeSessionValue(offerResponseStorageKey);
      winterSpecialApplied = false;
      try {
        localStorage.removeItem(offerStorageKey);
        localStorage.removeItem('seq_booking_offer_state_v1');
      } catch (error) {
        // Storage can be unavailable in privacy-restricted browsers.
      }
      updatePricing();
      containingBookingDialog.dispatchEvent(new CustomEvent('seq:offer-state-changed', {
        bubbles: false,
        detail: { applied: false }
      }));
    });
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) saveBookingDraft();
  });

  section.addEventListener('change', event => {
    if (event.target.matches('input')) markBookingStarted('details_entered');
    queueBookingDraftSave();
  });

  window.addEventListener('pagehide', saveBookingDraft);
  window.addEventListener('beforeunload', saveBookingDraft);
  window.addEventListener('resize', updateDateScrollerState, { passive: true });

  const restoredDraft = restoreBookingDraft();
  const restoredUnits = units;
  const restoredOutdoorUnits = outdoorUnits;
  const hasIncomingQuantityOverride = getPreselectedQuantities();
  const initialContext = incomingPersonalisationContext();
  returningCustomerProfile = loadReturningCustomerProfile();
  applyCustomerDetails(initialContext);
  if (!restoredDraft) {
    const initialSuburb = initialContext.suburb || returningCustomerProfile?.suburb || '';
    const initialSuburbOption = matchingSuburbOption(initialSuburb);
    if (initialSuburbOption && suburbSelect) suburbSelect.value = initialSuburbOption.value;
  }
  let preserveRestoredSelection = Boolean(restoredDraft);

  if (
    hasIncomingQuantityOverride &&
    restoredDraft &&
    (units !== restoredUnits || outdoorUnits !== restoredOutdoorUnits)
  ) {
    selectedDate = null;
    resetReservation(false);
    currentStep = Math.min(currentStep, 2);
    preserveRestoredSelection = false;
  }

  winterSpecialApplied =
    readLocalStorageValue(offerStorageKey) === 'applied' ||
    readLocalStorageValue('seq_booking_offer_state_v1') === 'applied' ||
    isWinterCampaignContext(initialContext);
  if (winterSpecialApplied) writeLocalStorageValue(offerStorageKey, 'applied');

  configureBookingJourneyLanguage();
  configureStepOneConversionProof();
  buildExperienceLayout();
  ensureStickyContexts();

  const liveSummaryForSticky = el('.seq-live-booking-summary');
  if (liveSummaryForSticky && 'IntersectionObserver' in window) {
    section.classList.add('is-live-summary-visible');
    const stickySummaryObserver = new IntersectionObserver(entries => {
      const entry = entries[0];
      section.classList.toggle(
        'is-live-summary-visible',
        Boolean(entry?.isIntersecting)
      );
    }, {
      root: containingBookingDialog?.querySelector('[data-seq-booking-scroll]') || null,
      threshold: 0.25
    });
    stickySummaryObserver.observe(liveSummaryForSticky);
  }

  ensureSuburbSearch();
  applyPersonalisationContext(initialContext, { initial: true });
  ensureInlineOffer();
  updatePricing();
  updateAppointmentGuidance();
  updateReview();

  void (async () => {
    await loadNextAvailableDates({
      preserveSelection: preserveRestoredSelection
    });

    showStep(currentStep);
    updatePricing();
    updateAppointmentGuidance();
    updateReview();
    queueBookingDraftSave();

    section.dispatchEvent(new CustomEvent('seq:booking-ready', { bubbles: true }));
    containingBookingDialog?.dispatchEvent(
      new CustomEvent('seq:booking-ready', { bubbles: false })
    );

    const incomingParams = new URLSearchParams(window.location.search);
    if (
      incomingParams.has('units') ||
      incomingParams.has('indoor') ||
      incomingParams.has('indoor_units')
    ) {
      window.requestAnimationFrame(() => {
        scrollBookingToTop();
      });
    }
  })();
}

window.SEQBookingOffer = window.SEQBookingOffer || {
  resetForTesting() {
    const reset = window.SEQBookingModal?.resetWinterOfferForTesting ||
      window.SEQBookingModal?.resetIdlePopupForTesting;
    reset?.();
  }
};
